import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import useSound from 'use-sound';
import { LogOut, Package, Layers, Copy, Check } from 'lucide-react';
import * as qzPrint from '../../utils/qzPrint';
import ScanInput from '../fulfillment/ScanInput';
import OrderDisplay from '../fulfillment/OrderDisplay';
import StoreShipmentPack, { storeTicketFrom } from './StoreShipmentPack';
import '../operations/DepartmentScreen.css';
import './PackStation.css';

const INACTIVITY_TIMEOUT = 90 * 60 * 1000; // 90 minutes

function PackStation() {
  // Auth state
  const [currentOperator, setCurrentOperator] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [showWelcome, setShowWelcome] = useState(false);
  const [showGoodbye, setShowGoodbye] = useState(false);
  const [goodbyeName, setGoodbyeName] = useState('');
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());

  // Mode: 'queue' | 'items' | 'boxing' | 'complete'
  const [mode, setMode] = useState('queue');
  const [storeShipment, setStoreShipment] = useState(null); // ship-from-store box (SHIP-####)

  // Queue state
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState({ packed_today: 0, in_progress: 0, queue_count: 0 });
  const [loading, setLoading] = useState(true);

  // Pack flow state
  const [currentTicket, setCurrentTicket] = useState(null);
  const [salesOrder, setSalesOrder] = useState(null);
  const [scannedItems, setScannedItems] = useState([]);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');

  // Boxing flow state - multi-box support
  const [boxTypes, setBoxTypes] = useState([]);
  const [packages, setPackages] = useState([{ id: 1, box: null, weight: '', manualWeight: false }]);
  const [activePackageIndex, setActivePackageIndex] = useState(0);
  const [rates, setRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(false);
  const [shipping, setShipping] = useState(false);
  const [shippingStep, setShippingStep] = useState('');

  // Insufficient-inventory prompt: shown when NetSuite blocks the fulfillment
  // because a packed SKU is short on-hand. `ctx` carries what's needed to
  // resume shipping (the UPS label was already bought in Step 1 — retrying
  // must NOT regenerate it) once inventory is corrected.
  const [insufficientInventory, setInsufficientInventory] = useState(null); // { items, ctx }
  const [adjustInputs, setAdjustInputs] = useState({}); // sku -> string qty
  const [adjustingSku, setAdjustingSku] = useState(null);
  const [adjustError, setAdjustError] = useState('');

  // Backward-compat accessors for active package
  const selectedBox = packages[activePackageIndex]?.box || null;
  const weight = packages[activePackageIndex]?.weight || '';
  const manualWeight = packages[activePackageIndex]?.manualWeight || false;
  const [isCapturingWeight, setIsCapturingWeight] = useState(false);

  // Edit box state
  const [editingBox, setEditingBox] = useState(null);
  const [editForm, setEditForm] = useState({ length: '', width: '', height: '' });

  // Add new box state
  const [showAddBox, setShowAddBox] = useState(false);
  const [newBoxForm, setNewBoxForm] = useState({ name: '', length: '', width: '', height: '' });

  // Manual fulfill modal state
  const [confirmItem, setConfirmItem] = useState(null);
  const [confirmQty, setConfirmQty] = useState(1);

  // Ship-to address check (repair + UPS validation) and the packer's choice
  const [addressCheck, setAddressCheck] = useState(null);
  const [acceptedAddress, setAcceptedAddress] = useState(null);

  // FBA state
  const [isFbaOrder, setIsFbaOrder] = useState(false);
  const [fbaLabels, setFbaLabels] = useState(null);
  const [fbaWeight, setFbaWeight] = useState('40');

  // Success state
  const [fulfillmentResult, setFulfillmentResult] = useState(null);
  const [labelData, setLabelData] = useState(null);

  // QZ Tray / Zebra printer state
  const [qzConnected, setQzConnected] = useState(false);
  const [qzSelectedPrinter, setQzSelectedPrinter] = useState('');
  const [printerIp, setPrinterIp] = useState(localStorage.getItem('zebraPrinterIp') || '');
  const [showPrinterConfig, setShowPrinterConfig] = useState(false);
  const [laserPrinter, setLaserPrinter] = useState(localStorage.getItem('laserPrinter') || '');
  const [availablePrinters, setAvailablePrinters] = useState([]);

  // Recent shipments state
  const [showRecentShipments, setShowRecentShipments] = useState(false);
  const [recentShipments, setRecentShipments] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [voidingId, setVoidingId] = useState(null);
  const [reshippingId, setReshippingId] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [recentSearch, setRecentSearch] = useState('');
  const [recentPage, setRecentPage] = useState(1);
  const [recentTotal, setRecentTotal] = useState(0);
  const [copiedTrackingId, setCopiedTrackingId] = useState(null);
  const [stuckTickets, setStuckTickets] = useState([]);
  const recentLimit = 50;

  // Batch fulfillment state
  const [batchOrders, setBatchOrders] = useState([]);
  const [batchMode, setBatchMode] = useState(false); // true = batch boxing mode
  const [batchCount, setBatchCount] = useState(0); // how many to process
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ processed: 0, total: 0, successful: 0, failed: 0 });
  const [batchResults, setBatchResults] = useState(null); // final results
  const [batchLastOrder, setBatchLastOrder] = useState(null); // last processed order info
  const [batchErrors, setBatchErrors] = useState([]); // error details per failed order

  // Sound effects
  const [playSuccess] = useSound('/sounds/success.mp3', { volume: 0.5 });
  const [playError] = useSound('/sounds/error.mp3', { volume: 0.5 });
  const [playComplete] = useSound('/sounds/complete.mp3', { volume: 0.7 });

  // Auto-focus management
  const scanInputRef = useRef(null);

  // Brand logo URLs for packing slips — served from /public so QZ's renderer can fetch directly.
  // Inlining as base64 balloons the data URI and can cause QZ to print a blank page.
  const logoUrls = {
    rewined: `${window.location.origin}/rewined_logo.jpg`,
    candlefish: `${window.location.origin}/candlefish_logo.jpg`,
  };

  // Load saved operator on mount
  useEffect(() => {
    const saved = localStorage.getItem('packingOperator');
    if (saved) {
      try {
        setCurrentOperator(JSON.parse(saved));
        setLastActivityTime(Date.now());
      } catch (e) {
        localStorage.removeItem('packingOperator');
      }
    }
  }, []);

  // Connect to QZ Tray on mount for Zebra printing + enumerate printers
  useEffect(() => {
    const connectToQZ = async () => {
      try {
        await qzPrint.setStation('pack');
        await qzPrint.connect();
        setQzConnected(true);
        const printers = await qzPrint.getPrinters();
        setAvailablePrinters(printers);
        const zebra = printers.find(p => p.toLowerCase().includes('zebra'));
        if (zebra) {
          setQzSelectedPrinter(zebra);
          console.log('PackStation: Auto-selected Zebra printer:', zebra);
        }
      } catch (err) {
        console.error('PackStation: QZ Tray connection failed:', err);
      }
    };
    connectToQZ();
  }, []);

  // Inactivity timeout
  useEffect(() => {
    if (!currentOperator) return;
    const check = setInterval(() => {
      if (Date.now() - lastActivityTime > INACTIVITY_TIMEOUT) {
        handleLogout();
      }
    }, 30000);
    return () => clearInterval(check);
  }, [currentOperator, lastActivityTime]);

  const resetActivity = useCallback(() => {
    setLastActivityTime(Date.now());
  }, []);

  // Auto-focus scan input
  useEffect(() => {
    if (scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [mode, salesOrder]);

  // === DATA FETCHING ===

  const fetchQueue = useCallback(async () => {
    try {
      const res = await axios.get('/api/packing/queue');
      setQueue(res.data);
    } catch (err) {
      console.error('Failed to fetch packing queue:', err);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get('/api/packing/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch packing stats:', err);
    }
  }, []);

  const loadAll = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    await Promise.all([fetchQueue(), fetchStats()]);
    if (showLoader) setLoading(false);
  }, [fetchQueue, fetchStats]);

  // Initial load + auto-refresh
  useEffect(() => {
    if (currentOperator && mode === 'queue') {
      loadAll();
      const interval = setInterval(() => loadAll(false), 30000);
      return () => clearInterval(interval);
    }
  }, [currentOperator, mode, loadAll]);

  // Fetch box types when entering boxing mode
  const fetchBoxTypes = useCallback(async () => {
    try {
      const res = await axios.get('/api/settings/shipping-boxes/active');
      setBoxTypes(res.data || []);
    } catch (err) {
      console.error('Failed to fetch box types:', err);
    }
  }, []);

  useEffect(() => {
    if (mode === 'boxing') {
      fetchBoxTypes();
    }
  }, [mode, fetchBoxTypes]);

  // Fetch rates when active package box + weight are set
  useEffect(() => {
    if (mode === 'boxing' && selectedBox && weight && salesOrder?.shipTo) {
      fetchRates();
    }
  }, [selectedBox, weight, activePackageIndex]);

  const fetchRates = async () => {
    if (!selectedBox || !weight || !salesOrder?.shipTo) return;
    setLoadingRates(true);
    try {
      const dims = `${selectedBox.length}x${selectedBox.width}x${selectedBox.height}`;
      const res = await axios.post('/api/fulfillment/get-rates', {
        shipTo: salesOrder.shipTo,
        packages: [{ weight: parseFloat(weight), dimensions: dims }]
      });
      if (res.data.success && res.data.rates) {
        setRates(res.data.rates);
      }
    } catch (err) {
      console.error('Failed to fetch rates:', err);
    } finally {
      setLoadingRates(false);
    }
  };

  // === MULTI-BOX HELPERS ===

  const updatePackage = (index, updates) => {
    setPackages(prev => prev.map((pkg, i) => i === index ? { ...pkg, ...updates } : pkg));
  };

  const setSelectedBox = (box) => updatePackage(activePackageIndex, { box });
  const setWeight = (w) => updatePackage(activePackageIndex, { weight: w });
  const setManualWeight = (mw) => updatePackage(activePackageIndex, { manualWeight: mw });

  const addPackage = () => {
    const nextId = Math.max(...packages.map(p => p.id)) + 1;
    setPackages(prev => [...prev, { id: nextId, box: null, weight: '', manualWeight: false }]);
    setActivePackageIndex(packages.length);
  };

  const removePackage = (index) => {
    if (packages.length <= 1) return;
    setPackages(prev => prev.filter((_, i) => i !== index));
    if (activePackageIndex >= index && activePackageIndex > 0) {
      setActivePackageIndex(activePackageIndex - 1);
    }
  };

  const allPackagesReady = packages.every(pkg => pkg.box && pkg.weight && parseFloat(pkg.weight) > 0);

  // === PIN AUTH ===

  const handlePinDigit = (digit) => {
    if (pinInput.length < 4) {
      const newPin = pinInput + digit;
      setPinInput(newPin);
      setPinError('');
      if (newPin.length === 4) {
        submitPin(newPin);
      }
    }
  };

  const handlePinClear = () => {
    setPinInput('');
    setPinError('');
  };

  const submitPin = async (pin) => {
    try {
      const res = await axios.post('/api/operators/pin', { pin });
      const op = res.data;
      setCurrentOperator(op);
      localStorage.setItem('packingOperator', JSON.stringify(op));
      setLastActivityTime(Date.now());
      setShowWelcome(true);
      setTimeout(() => setShowWelcome(false), 2500);
      setPinInput('');
    } catch (err) {
      setPinError('Invalid PIN');
      setPinInput('');
    }
  };

  const handleLogout = () => {
    if (currentOperator) {
      setGoodbyeName(currentOperator.first_name);
      setShowGoodbye(true);
      setTimeout(() => {
        setShowGoodbye(false);
        setGoodbyeName('');
      }, 2000);
    }
    setCurrentOperator(null);
    localStorage.removeItem('packingOperator');
    setPinInput('');
    resetPackFlow();
  };

  // === PACK FLOW ===

  // Check for identical unfulfilled orders (batch detection)
  // Ask the server to repair the NetSuite address and run it past UPS. Purely
  // advisory — it buys nothing and never blocks the pack flow, so a failure here
  // is swallowed and the order ships exactly as it would have.
  const checkShipAddress = async (orderData) => {
    setAddressCheck(null);
    setAcceptedAddress(null);
    const shipTo = orderData?.shipTo;
    if (!shipTo?.addr1 && !shipTo?.zip) return;
    try {
      const res = await axios.post('/api/fulfillment/validate-address', {
        shipTo,
        orderNumber: orderData.orderNumber || orderData.id
      });
      setAddressCheck(res.data);
    } catch (err) {
      console.log('Address check unavailable:', err.message);
    }
  };

  const checkForIdenticalOrders = async (orderData) => {
    try {
      const items = (orderData.items || []).map(i => ({
        sku: i.sku,
        quantity: Number(i.quantity)
      }));
      if (!items.length) return;

      const res = await axios.post('/api/packing/batch/find-identical', {
        items,
        currentOrderNumber: orderData.orderNumber
      });

      if (res.data.count > 0) {
        setBatchOrders(res.data.orders);
        console.log(`Batch detection: ${res.data.count} identical orders found`);
      } else {
        setBatchOrders([]);
      }
    } catch (err) {
      console.error('Batch detection failed (non-blocking):', err.message);
      setBatchOrders([]);
    }
  };

  const startPacking = async (ticketNumber) => {
    resetActivity();
    setLoading(true);
    setError(null);
    setBatchOrders([]);
    setBatchMode(false);
    setBatchResults(null);

    try {
      const scanRes = await axios.post('/api/packing/scan', {
        ticketNumber,
        packerId: currentOperator?.id,
        packerName: currentOperator?.display_name || currentOperator?.first_name
      });
      const ticket = scanRes.data;
      setCurrentTicket(ticket);

      const orderRes = await axios.get(`/api/packing/order/${ticket.id}`);
      setSalesOrder(orderRes.data);
      setScannedItems([]);

      // Detect FBA inbound orders
      const fba = ticket.order_type === 'fba_inbound' || orderRes.data.orderType === 'fba_inbound';
      setIsFbaOrder(fba);

      if (fba) {
        setMode('boxing'); // Skip item scanning for FBA — go straight to boxing
        setFbaWeight('40');
        setMessage(`FBA Inbound loaded: ${orderRes.data.items?.length || 0} SKUs, ${orderRes.data.totalUnits || 0} units.`);
      } else {
        setMode('items');
        // Show inventory warnings if any items have bin issues
        const warnings = orderRes.data.inventoryWarnings;
        if (warnings && warnings.length > 0) {
          const warnSkus = warnings.map(w => w.sku).join(', ');
          setError(`WARNING: ${warnSkus} — no bin inventory in NetSuite. Fulfillment will fail. Adjust inventory before shipping.`);
          playError();
        }
        setMessage(`Order ${ticket.tran_id} loaded. Start scanning items.`);
      }
      if (!orderRes.data.inventoryWarnings?.length) playSuccess();

      // Check the ship-to against UPS in the background, so an address problem
      // shows up now instead of as a UPS error after the boxes are packed.
      checkShipAddress(orderRes.data);

      // Check for identical orders in background (not for FBA)
      if (!fba) checkForIdenticalOrders(orderRes.data);
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to start packing';
      setError(errMsg);
      playError();
    } finally {
      setLoading(false);
    }
  };

  // A ship-from-store box: the bag slip QR (Run check-in URL with ?checkin=SHIP-####)
  // or a typed SHIP-#### opens the store shipment instead of a sales order.
  const startStoreShipment = async (ticket) => {
    resetActivity();
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`/api/market-shipments/by-ticket/${encodeURIComponent(ticket)}`);
      setStoreShipment(r.data.shipment);
      setMode('storeShip');
      playSuccess();
    } catch (err) {
      setError(err.response?.data?.error || `Could not load store shipment ${ticket}`);
      playError();
    } finally {
      setLoading(false);
    }
  };

  const handleTicketScan = (value) => {
    const storeTicket = storeTicketFrom(value);
    if (storeTicket) startStoreShipment(storeTicket);
    else startPacking(value);
  };

  const handleItemScan = (itemSku) => {
    if (!salesOrder) return;
    resetActivity();

    if (itemSku.toLowerCase() === 'undo' || itemSku === '999999') {
      handleUndo();
      return;
    }

    const scanVal = itemSku.trim().toLowerCase();
    const orderItem = salesOrder.items.find(item => {
      const sku = (item.sku || '').toLowerCase();
      const upc = (item.upc || '').toLowerCase();
      return sku === scanVal || upc === scanVal || sku.includes(scanVal) || scanVal.includes(sku);
    });

    if (!orderItem) {
      console.log('No match for scan:', itemSku, 'Order SKUs:', salesOrder.items.map(i => i.sku));
      setError(`Item ${itemSku} not in this order`);
      playError();
      return;
    }

    const scannedQty = scannedItems.filter(item => item.sku === orderItem.sku).length;
    if (scannedQty >= orderItem.quantity) {
      setError(`Item ${itemSku} already fully scanned`);
      playError();
      return;
    }

    const scannedItem = {
      ...orderItem,
      timestamp: new Date().toISOString()
    };

    const newScanned = [...scannedItems, scannedItem];
    setScannedItems(newScanned);
    playSuccess();
    setMessage(`Scanned: ${orderItem.description}`);
    setError(null);

    const totalRequired = salesOrder.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    if (newScanned.length === totalRequired) {
      playComplete();
      // Auto-advance to boxing mode
      setTimeout(() => {
        setPackages([{ id: 1, box: null, weight: '', manualWeight: false }]);
        setActivePackageIndex(0);
        setRates([]);
        setFulfillmentResult(null);
        setLabelData(null);
        setMode('boxing');
        setMessage('All items scanned! Select a box and enter weight.');
        setError(null);
      }, 400);
    }
  };

  const handleUndo = () => {
    if (scannedItems.length === 0) {
      setError('No items to undo');
      playError();
      return;
    }
    const lastItem = scannedItems[scannedItems.length - 1];
    setScannedItems(scannedItems.slice(0, -1));
    playSuccess();
    setMessage(`Removed: ${lastItem.description}`);
    setError(null);
  };

  // Manual fulfill - tap a line item to confirm
  const handleItemTap = (item, progress) => {
    resetActivity();
    const remaining = progress.total - progress.scanned;
    setConfirmItem(item);
    setConfirmQty(remaining);
  };

  const handleConfirmFulfill = () => {
    if (!confirmItem || confirmQty < 1) return;
    resetActivity();

    const qty = Math.min(confirmQty, Number(confirmItem.quantity) - scannedItems.filter(s => s.sku === confirmItem.sku).length);
    const newItems = [];
    for (let i = 0; i < qty; i++) {
      newItems.push({ ...confirmItem, timestamp: new Date().toISOString() });
    }

    const updated = [...scannedItems, ...newItems];
    setScannedItems(updated);
    playSuccess();
    setMessage(`Fulfilled ${qty}x ${confirmItem.description}`);
    setError(null);
    setConfirmItem(null);

    const totalRequired = salesOrder.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    if (updated.length === totalRequired) {
      playComplete();
      setTimeout(() => {
        setPackages([{ id: 1, box: null, weight: '', manualWeight: false }]);
        setActivePackageIndex(0);
        setRates([]);
        setFulfillmentResult(null);
        setLabelData(null);
        setMode('boxing');
        setMessage('All items fulfilled! Select a box and enter weight.');
        setError(null);
      }, 400);
    }
  };

  const handleProceedToBoxing = () => {
    resetActivity();
    setPackages([{ id: 1, box: null, weight: '', manualWeight: false }]);
    setActivePackageIndex(0);
    setRates([]);
    setFulfillmentResult(null);
    setLabelData(null);
    setMode('boxing');
    setMessage('Select a box and enter weight');
    setError(null);
  };

  const handleBackToScanning = () => {
    setMode('items');
    setMessage('Continue scanning items');
  };

  // === BOXING FLOW ===

  const captureWeight = async () => {
    setIsCapturingWeight(true);
    try {
      const res = await axios.get('/api/scale/weight');
      if (res.data && res.data.weight) {
        setWeight(res.data.weight.toFixed(2));
        setMessage(`Weight captured: ${res.data.weight.toFixed(2)} lbs${res.data.simulated ? ' (simulated)' : ''}`);
      }
    } catch (err) {
      console.error('Scale error:', err);
      setMessage('Scale read failed. Use manual entry.');
      setManualWeight(true);
    } finally {
      setIsCapturingWeight(false);
    }
  };

  const handleEditBox = (box) => {
    setEditingBox(box.id);
    setEditForm({ length: box.length, width: box.width, height: box.height });
  };

  const handleSaveBoxEdit = async () => {
    try {
      await axios.put(`/api/packing/boxes/${editingBox}`, {
        length: parseFloat(editForm.length),
        width: parseFloat(editForm.width),
        height: parseFloat(editForm.height)
      });
      setEditingBox(null);
      fetchBoxTypes();
      // If this was the selected box, update it
      if (selectedBox?.id === editingBox) {
        setSelectedBox(prev => ({
          ...prev,
          length: parseFloat(editForm.length),
          width: parseFloat(editForm.width),
          height: parseFloat(editForm.height)
        }));
      }
    } catch (err) {
      console.error('Failed to save box:', err);
      setError('Failed to save box dimensions');
    }
  };

  const handleAddNewBox = async () => {
    if (!newBoxForm.name || !newBoxForm.length || !newBoxForm.width || !newBoxForm.height) {
      setError('Fill in all fields for new box');
      return;
    }
    try {
      await axios.post('/api/packing/boxes', {
        name: newBoxForm.name,
        length: parseFloat(newBoxForm.length),
        width: parseFloat(newBoxForm.width),
        height: parseFloat(newBoxForm.height)
      });
      setShowAddBox(false);
      setNewBoxForm({ name: '', length: '', width: '', height: '' });
      fetchBoxTypes();
    } catch (err) {
      console.error('Failed to add box:', err);
      setError('Failed to add new box');
    }
  };

  // === COMPLETE WITHOUT UPS (local delivery / customer pickup) ===

  const handleCompleteLocal = async () => {
    if (!allPackagesReady) {
      setError('All boxes need a box type and weight');
      return;
    }
    resetActivity();
    setShipping(true);
    setError(null);

    try {
      const pkgCount = packages.length;
      setShippingStep('Creating NetSuite fulfillment (no UPS)...');

      // What was actually scanned/packed, not the full order — lets
      // fulfillmentService ship exactly this much and leave the rest open
      // (see "Fulfill Incomplete & Box").
      const aggregated = getAggregatedItems();

      const fulfillRes = await axios.post('/api/fulfillment/create', {
        salesOrderId: salesOrder.id,
        orderNumber: salesOrder.orderNumber,
        customerId: salesOrder.customerId,
        items: aggregated.map(i => ({ sku: i.sku, quantity: i.quantity })),
        packages: packages.map(pkg => ({ weight: parseFloat(pkg.weight) || 1 }))
      });
      const fId = fulfillRes.data?.fulfillmentId || null;
      const fulfillmentNumber = fulfillRes.data?.fulfillmentNumber || null;
      const totalRequired = salesOrder.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const totalPacked = aggregated.reduce((sum, i) => sum + Number(i.quantity || 0), 0);
      const partialNote = totalPacked < totalRequired ? ' (partial — remainder stays open on the order)' : '';

      setShippingStep('Completing...');
      const completePackages = packages.map((pkg) => ({
        boxName: pkg.box.name,
        boxDimensions: `${pkg.box.length}x${pkg.box.width}x${pkg.box.height}`,
        weight: parseFloat(pkg.weight),
        trackingNumber: null,
        labelData: null,
        shippingCost: 0,
        retailShippingCost: null,
        serviceCode: null,
        shipmentId: null
      }));

      await axios.post(`/api/packing/complete/${currentTicket.id}`, {
        trackingNumber: null,
        weight: parseFloat(packages[0].weight),
        boxName: packages[0].box.name,
        boxDimensions: `${packages[0].box.length}x${packages[0].box.width}x${packages[0].box.height}`,
        serviceCode: null,
        serviceName: salesOrder.shipMethodName || localOrPickupLabel,
        shippingCost: 0,
        retailShippingCost: null,
        fulfillmentId: fId,
        labelData: null,
        packedItems: aggregated.map(i => ({ sku: i.sku, description: i.description, quantity: i.quantity })),
        packages: completePackages
      });

      setFulfillmentResult({ fulfillmentId: fId, fulfillmentNumber });
      setLabelData(null);
      setMode('complete');
      playComplete();
      setMessage(`${localOrPickupLabel} order completed${pkgCount > 1 ? ` (${pkgCount} boxes)` : ''}!${partialNote} No UPS label printed.`);

      // Auto-print a name/address tag to the Zebra — no UPS label gets bought
      // for Local Delivery / Customer Pickup, so this is the only physical
      // tag on the box for the driver or the customer at will-call.
      const tagPrinted = await printDeliveryTag(pkgCount);
      if (!tagPrinted) {
        setMessage(`${localOrPickupLabel} order completed${pkgCount > 1 ? ` (${pkgCount} boxes)` : ''}!${partialNote} Click "Print Tag" to print the delivery tag.`);
      }

      // Auto-print packing slip only (no shipping label needed)
      if (qzConnected && laserPrinter) {
        try {
          const slipHTML = buildPackingSlipHTML(
            salesOrder,
            aggregated,
            '',
            { fulfillmentId: fId, fulfillmentNumber },
            pkgCount
          );
          await qzPrint.printHTML(slipHTML, laserPrinter);
        } catch (slipErr) {
          console.error('Auto-print packing slip failed:', slipErr.message);
        }
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || 'Completion failed';
      setError(errMsg);
      playError();
    } finally {
      setShipping(false);
      setShippingStep('');
    }
  };

  // === SHIP IT (multi-box) ===

  const isLocalOrPickup = !!(salesOrder?.isLocalDelivery || salesOrder?.isCustomerPickup);
  const localOrPickupLabel = salesOrder?.isCustomerPickup
    ? 'Customer Pickup'
    : salesOrder?.isLocalDelivery
    ? 'Local Delivery'
    : null;

  // Step 2 onward: create the NetSuite fulfillment against a UPS label that's
  // ALREADY been bought (Step 1, in handleShipIt). Split out so an
  // INSUFFICIENT_INVENTORY failure here can pause for the operator to fix
  // on-hand and resume from this step — retrying handleShipIt from scratch
  // would buy a second, unused UPS label.
  const finishShipment = async (ctx, { forceShip = false } = {}) => {
    const { labelRes, labelPackages, primaryTracking, brandItems, pkgCount, partialNote } = ctx;

    let fId = null;
    let fulfillmentNumber = null;
    setShippingStep('Creating NetSuite fulfillment...');
    let fulfillRes;
    try {
      fulfillRes = await axios.post('/api/fulfillment/create', {
        salesOrderId: salesOrder.id,
        orderNumber: salesOrder.orderNumber,
        customerId: salesOrder.customerId,
        items: brandItems.map(i => ({ sku: i.sku, quantity: i.quantity })),
        packages: packages.map(pkg => ({
          weight: parseFloat(pkg.weight) || 1
        })),
        ...(forceShip ? { forceShip: true } : {})
      });
    } catch (err) {
      if (err.response?.data?.errorType === 'INSUFFICIENT_INVENTORY') {
        const items = err.response.data.insufficientItems || [];
        setInsufficientInventory({ items, ctx });
        setAdjustInputs(Object.fromEntries(items.map(it => [it.sku, String(it.needed ?? '')])));
        setAdjustError('');
        return; // pause — modal takes over; Retry re-invokes finishShipment(ctx)
      }
      throw err;
    }
    fId = fulfillRes.data?.fulfillmentId || null;
    fulfillmentNumber = fulfillRes.data?.fulfillmentNumber || null;

    // Step 2b: Add tracking numbers to the NS fulfillment.
    // The RESTlet can return HTTP 200 with {success:false} on internal failure,
    // so we have to inspect the body — don't just rely on axios throwing.
    if (fId && primaryTracking) {
      try {
        setShippingStep('Adding tracking to fulfillment...');
        const pkgDetails = labelPackages.map((lp, i) => ({
          trackingNumber: lp.trackingNumber,
          weight: parseFloat(packages[i]?.weight) || 1
        })).filter(p => p.trackingNumber);
        const trackRes = await axios.post(`/api/packing/update-tracking/${fId}`, {
          trackingNumber: primaryTracking,
          packages: pkgDetails.length > 1 ? pkgDetails : undefined
        });
        if (trackRes.data && trackRes.data.success === false) {
          console.error('RESTlet reported failure adding tracking:', trackRes.data);
          setMessage(`Order shipped but NetSuite tracking push failed — fix in NS manually. (${trackRes.data.message || 'RESTlet error'})`);
        }
      } catch (trackErr) {
        console.error('Failed to add tracking to NS fulfillment:', trackErr.message);
        setMessage(`Order shipped but NetSuite tracking push failed — fix in NS manually. (${trackErr.message})`);
      }
    }

    // Step 3: Complete packing with all package data
    setShippingStep('Completing...');
    const aggregated = getAggregatedItems();
    const completePackages = packages.map((pkg, i) => ({
      boxName: pkg.box.name,
      boxDimensions: `${pkg.box.length}x${pkg.box.width}x${pkg.box.height}`,
      weight: parseFloat(pkg.weight),
      trackingNumber: labelPackages[i]?.trackingNumber || null,
      labelData: labelPackages[i]?.labelData || null,
      shippingCost: labelPackages[i]?.cost || null,
      retailShippingCost: labelPackages[i]?.retailCost || null,
      serviceCode: labelPackages[i]?.serviceCode || '03',
      shipmentId: labelPackages[i]?.shipmentId || null
    }));

    const totalCost = completePackages.reduce((sum, p) => sum + (parseFloat(p.shippingCost) || 0), 0);

    await axios.post(`/api/packing/complete/${currentTicket.id}`, {
      trackingNumber: primaryTracking,
      weight: parseFloat(packages[0].weight),
      boxName: packages[0].box.name,
      boxDimensions: `${packages[0].box.length}x${packages[0].box.width}x${packages[0].box.height}`,
      serviceCode: labelPackages[0]?.serviceCode || '03',
      serviceName: labelPackages[0]?.service || 'UPS Ground',
      shippingCost: totalCost,
      retailShippingCost: completePackages.reduce((sum, p) => sum + (parseFloat(p.retailShippingCost) || 0), 0) || null,
      fulfillmentId: fId,
      labelData: labelPackages[0]?.labelData || null,
      packedItems: aggregated.map(i => ({ sku: i.sku, description: i.description, quantity: i.quantity })),
      packages: completePackages
    });

    // Success - store all label data for the complete screen
    setFulfillmentResult({ fulfillmentId: fId, fulfillmentNumber });
    setLabelData({
      ...labelRes.data,
      packages: labelPackages,
      totalCost
    });
    setMode('complete');
    playComplete();
    setMessage(`Order shipped${pkgCount > 1 ? ` (${pkgCount} boxes)` : ''}!${partialNote}`
      + (fulfillRes.data?.nsPending ? ' NetSuite fulfillment is pending — will be reconciled once stock catches up.' : ''));

    // Auto-print all labels to Zebra via network (server-side)
    if (labelPackages.some(p => p.labelData)) {
      let printed = false;
      try {
        const printerIp = localStorage.getItem('zebraPrinterIp');
        if (printerIp) {
          await axios.post(`/api/packing/print-label/${currentTicket.id}`, { printerIp });
          console.log(`${labelPackages.length} label(s) printed via network printer`);
          printed = true;
        }
      } catch (netErr) {
        console.error('Network print failed:', netErr.message);
      }
      // Fallback to QZ Tray - print each label
      if (!printed && qzConnected) {
        try {
          for (const lp of labelPackages) {
            if (lp.labelData) {
              const zpl = atob(lp.labelData);
              await qzPrint.printZPL(zpl, qzSelectedPrinter || null);
            }
          }
          console.log('Labels printed via QZ Tray');
          printed = true;
        } catch (qzErr) {
          console.error('QZ print failed:', qzErr.message);
        }
      }
      if (!printed) {
        setMessage(`Order shipped${pkgCount > 1 ? ` (${pkgCount} boxes)` : ''}!${partialNote} Click "Print Labels" to print.`);
      }
    }

    // Auto-print packing slip to laser printer
    if (qzConnected && laserPrinter) {
      try {
        const allTracking = labelPackages.map(p => p.trackingNumber).filter(Boolean);
        const slipHTML = buildPackingSlipHTML(
          salesOrder,
          aggregated,
          allTracking.join(', '),
          { fulfillmentId: fId, fulfillmentNumber },
          pkgCount
        );
        await qzPrint.printHTML(slipHTML, laserPrinter);
        console.log('Packing slip printed via QZ Tray to laser');
      } catch (slipErr) {
        console.error('Auto-print packing slip failed:', slipErr.message);
      }
    }
  };

  const handleShipIt = async () => {
    if (!allPackagesReady) {
      setError('All boxes need a box type and weight');
      return;
    }

    // Local delivery / customer pickup: skip UPS label, just create the NS fulfillment
    // and mark the pick ticket fulfilled. Asking for explicit confirm so we don't
    // silently change behavior from the operator's expectation.
    if (isLocalOrPickup) {
      const confirmed = window.confirm(
        `This order is "${salesOrder.shipMethodName || localOrPickupLabel}" per NetSuite.\n\n` +
        `Complete WITHOUT a UPS label? (No UPS charge, no shipping label printed.)\n\n` +
        `Click Cancel if you actually need a UPS label anyway.`
      );
      if (!confirmed) return; // let them fall through to the normal UPS path below
      return handleCompleteLocal();
    }

    resetActivity();
    setShipping(true);
    setError(null);
    setInsufficientInventory(null);

    try {
      const pkgCount = packages.length;

      // Step 1: Generate shipping labels from UPS FIRST (validates address before creating fulfillment)
      setShippingStep(`Generating label${pkgCount > 1 ? ` 1 of ${pkgCount}` : ''}...`);

      const brandItems = getAggregatedItems();
      const brand = brandItems.length > 0 && brandItems.every(i => (i.sku || '').toUpperCase().startsWith('CF')) ? 'candlefish' : 'rewined';
      const totalRequired = salesOrder.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const totalPacked = brandItems.reduce((sum, i) => sum + Number(i.quantity || 0), 0);
      const partialNote = totalPacked < totalRequired ? ' (partial — remainder stays open on the order)' : '';

      // If the packer accepted the UPS-corrected address, that is what gets the
      // label. Otherwise the server repairs/validates the NetSuite address itself.
      const labelRes = await axios.post('/api/fulfillment/generate-label', {
        salesOrderId: salesOrder.id,
        shipTo: acceptedAddress
          ? { ...salesOrder.shipTo, ...acceptedAddress, addr1: acceptedAddress.address1, addr2: acceptedAddress.address2 }
          : salesOrder.shipTo,
        serviceCode: '03', // fallback; server upgrades from shipMethodName when expedited
        shipMethodName: salesOrder.shipMethodName,
        brand,
        packages: packages.map(pkg => ({
          weight: parseFloat(pkg.weight),
          dimensions: `${pkg.box.length}x${pkg.box.width}x${pkg.box.height}`
        }))
      });

      const labelPackages = labelRes.data.packages || [labelRes.data];
      const primaryTracking = labelPackages[0]?.trackingNumber || null;

      await finishShipment({ labelRes, labelPackages, primaryTracking, brandItems, pkgCount, partialNote });
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || 'Shipping failed';
      setError(errMsg);
      playError();
    } finally {
      setShipping(false);
      setShippingStep('');
    }
  };

  // Re-attempt Step 2+ after the operator corrects on-hand from the
  // insufficient-inventory modal. Reuses the UPS label already bought.
  const handleRetryAfterAdjust = async () => {
    const ctx = insufficientInventory?.ctx;
    if (!ctx) return;
    setInsufficientInventory(null);
    setShipping(true);
    setError(null);
    try {
      await finishShipment(ctx);
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || 'Shipping failed';
      setError(errMsg);
      playError();
    } finally {
      setShipping(false);
      setShippingStep('');
    }
  };

  // We really don't have the stock to add. Ship anyway: skips NetSuite's
  // inventory check by recording the shipment natively instead (label's
  // already bought, customer needs their tracking) — the real NetSuite
  // fulfillment gets posted later once someone reconciles the shortfall.
  const handleShipAnyway = async () => {
    const ctx = insufficientInventory?.ctx;
    if (!ctx) return;
    setInsufficientInventory(null);
    setShipping(true);
    setError(null);
    try {
      await finishShipment(ctx, { forceShip: true });
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || 'Shipping failed';
      setError(errMsg);
      playError();
    } finally {
      setShipping(false);
      setShippingStep('');
    }
  };

  const handleQuickAdjust = async (sku) => {
    const qty = parseFloat(adjustInputs[sku]);
    if (!Number.isFinite(qty) || qty < 0) {
      setAdjustError('Enter a valid non-negative quantity');
      return;
    }
    setAdjustingSku(sku);
    setAdjustError('');
    try {
      const res = await axios.post('/api/packing/quick-adjust-inventory', {
        sku,
        newQuantityOnHand: qty,
        operatorName: currentOperator?.display_name || currentOperator?.first_name
      });
      const unbinned = !!res.data?.unbinned;
      const fallbackBinCode = res.data?.fallbackBinUsed ? res.data.fallbackBinCode : null;
      setInsufficientInventory(prev => prev ? {
        ...prev,
        items: prev.items.map(it => it.sku === sku ? { ...it, adjusted: true, available: qty, unbinned, fallbackBinCode } : it)
      } : prev);
    } catch (err) {
      setAdjustError(err.response?.data?.error || err.message || 'Inventory adjustment failed');
    } finally {
      setAdjustingSku(null);
    }
  };

  // === BATCH FULFILLMENT ===

  const handleEnterBatchMode = () => {
    resetActivity();
    setBatchMode(true);
    setBatchCount(Math.min(batchOrders.length, 50)); // Default to 50 or total
    setPackages([{ id: 1, box: null, weight: '', manualWeight: false }]);
    setActivePackageIndex(0);
    setRates([]);
    setFulfillmentResult(null);
    setLabelData(null);
    setMode('boxing');
    setMessage(`Batch mode: Select box and weight for ${batchOrders.length} identical orders`);
    setError(null);
  };

  const handleBatchShip = async () => {
    if (!selectedBox || !weight || parseFloat(weight) <= 0) {
      setError('Select a box and enter weight first');
      return;
    }
    if (batchCount < 1) {
      setError('Select at least 1 order to process');
      return;
    }

    resetActivity();
    setBatchProcessing(true);
    setBatchProgress({ processed: 0, total: batchCount, successful: 0, failed: 0 });
    setBatchLastOrder(null);
    setBatchErrors([]);
    setError(null);

    try {
      const ordersToProcess = batchOrders.slice(0, batchCount);
      const printerIpVal = localStorage.getItem('zebraPrinterIp') || '';
      const brandItems = getAggregatedItems();
      const brand = brandItems.length > 0 && brandItems.every(i => (i.sku || '').toUpperCase().startsWith('CF')) ? 'candlefish' : 'rewined';

      const response = await fetch('/api/packing/batch/process', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orders: ordersToProcess,
          boxConfig: {
            name: selectedBox.name,
            length: selectedBox.length,
            width: selectedBox.width,
            height: selectedBox.height
          },
          weight: parseFloat(weight),
          serviceCode: '03',
          brand,
          operatorId: currentOperator?.id,
          operatorName: currentOperator?.display_name || currentOperator?.first_name,
          printerIp: printerIpVal || undefined
        })
      });

      // Read NDJSON stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const event = JSON.parse(line);

            if (event.type === 'progress') {
              setBatchProgress({
                processed: event.processed,
                total: event.total,
                successful: event.successful,
                failed: event.failed
              });
              setBatchLastOrder(event.order);

              // Print label via QZ Tray if no server-side printer
              if (!printerIpVal && qzConnected && event.order?.labelData) {
                try {
                  const zpl = atob(event.order.labelData);
                  await qzPrint.printZPL(zpl, qzSelectedPrinter || null);
                } catch (printErr) {
                  console.error('QZ print failed:', printErr.message);
                }
              }
            } else if (event.type === 'error') {
              setBatchProgress(prev => ({
                ...prev,
                processed: event.processed,
                failed: event.failed
              }));
              setBatchErrors(prev => [...prev, { orderNumber: event.orderNumber, error: event.error }]);
            } else if (event.type === 'complete') {
              setBatchResults({
                total: event.total,
                successful: event.successful,
                failed: event.failed,
                totalCost: event.totalCost
              });
              playComplete();
            }
          } catch (parseErr) {
            console.error('Failed to parse batch event:', line);
          }
        }
      }
    } catch (err) {
      setError(`Batch processing failed: ${err.message}`);
      playError();
    } finally {
      setBatchProcessing(false);
    }
  };

  const handleBatchDone = () => {
    setBatchMode(false);
    setBatchOrders([]);
    setBatchResults(null);
    setBatchProgress({ processed: 0, total: 0, successful: 0, failed: 0 });
    setBatchLastOrder(null);
    setBatchCount(0);
    setBatchErrors([]);
    resetPackFlow();
  };

  // === PRINT HELPERS ===

  // Local Delivery / Customer Pickup tag — no UPS label gets bought for these
  // ship methods, so this is the only physical name/address tag on the box.
  // Returns true if it made it to a printer.
  const printDeliveryTag = async (boxCount) => {
    if (!salesOrder) return false;
    try {
      const printerIp = localStorage.getItem('zebraPrinterIp');
      const tagRes = await axios.post('/api/packing/print-delivery-tag', {
        printerIp,
        label: localOrPickupLabel,
        orderNumber: salesOrder.orderNumber,
        customerName: salesOrder.customerName || salesOrder.shipTo?.name || '',
        addr1: salesOrder.shipTo?.addr1 || salesOrder.shipAddress1 || '',
        addr2: salesOrder.shipTo?.addr2 || '',
        city: salesOrder.shipTo?.city || salesOrder.shipCity || '',
        state: salesOrder.shipTo?.state || salesOrder.shipState || '',
        zip: salesOrder.shipTo?.zip || salesOrder.shipZip || '',
        phone: salesOrder.shipTo?.phone || '',
        memo: salesOrder.specialInstructions || '',
        boxCount: boxCount || packages.length || 1
      });
      if (tagRes.data?.success) return true;
      if (qzConnected && tagRes.data?.zpl) {
        await qzPrint.printZPL(tagRes.data.zpl, qzSelectedPrinter || null);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Delivery tag print failed:', err.message);
      return false;
    }
  };

  const printLabel = async () => {
    const allLabels = labelData?.packages || (labelData?.labelData ? [labelData] : []);
    if (allLabels.length === 0 && !currentTicket?.id) return;

    try {
      // Try server-side network print first (prints all labels from DB)
      const printerIp = localStorage.getItem('zebraPrinterIp');
      if (printerIp && currentTicket?.id) {
        try {
          await axios.post(`/api/packing/print-label/${currentTicket.id}`, { printerIp });
          setMessage(`${allLabels.length > 1 ? `${allLabels.length} labels` : 'Label'} sent to printer`);
          return;
        } catch (netErr) {
          console.log('Server-side print failed:', netErr.message);
        }
      }

      // Get ZPL content from in-memory label data
      const labelContents = allLabels.map(l => l?.labelData).filter(Boolean);
      if (labelContents.length === 0) {
        setError('No label data available. Try reprinting from the queue.');
        return;
      }

      // Try QZ Tray - print all labels
      if (qzConnected) {
        for (const ld of labelContents) {
          const zplContent = atob(ld);
          await qzPrint.printZPL(zplContent, qzSelectedPrinter || null);
        }
        setMessage(`${labelContents.length > 1 ? `${labelContents.length} labels` : 'Label'} printed`);
        return;
      }

      // Fallback: try server-side TCP print with raw ZPL
      if (printerIp) {
        try {
          for (const ld of labelContents) {
            const zplContent = atob(ld);
            await axios.post('/api/print/zpl', { zpl: zplContent, printerIp });
          }
          setMessage(`${labelContents.length > 1 ? `${labelContents.length} labels` : 'Label'} sent to printer`);
          return;
        } catch (netErr) {
          console.log('Network print failed:', netErr.message);
        }
      }

      // Last resort: download .zpl files
      labelContents.forEach((ld, i) => {
        const zplContent = atob(ld);
        const blob = new Blob([zplContent], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const tracking = allLabels[i]?.trackingNumber || 'shipping';
        a.download = `label-${tracking}${labelContents.length > 1 ? `-box${i + 1}` : ''}.zpl`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
    } catch (err) {
      console.error('Failed to print label:', err);
      setError(`Print failed: ${err.message}`);
    }
  };

  const printPackingSlip = async () => {
    // Try QZ Tray laser printer first
    if (qzConnected && laserPrinter) {
      try {
        const aggregated = getAggregatedItems();
        const allLabelPkgs = labelData?.packages || [labelData];
        const allTracking = allLabelPkgs.map(p => p?.trackingNumber).filter(Boolean).join(', ');
        const html = buildPackingSlipHTML(
          salesOrder,
          aggregated,
          allTracking,
          fulfillmentResult,
          allLabelPkgs.length
        );
        await qzPrint.printHTML(html, laserPrinter);
        setMessage('Packing slip sent to laser printer');
        return;
      } catch (err) {
        console.error('QZ laser print failed, falling back to browser:', err.message);
      }
    }
    // Fallback to browser print dialog
    window.print();
  };

  const handleCancelPacking = async () => {
    resetActivity();
    if (currentTicket) {
      try {
        await axios.post(`/api/packing/cancel/${currentTicket.id}`);
      } catch (err) {
        console.error('Failed to cancel packing:', err);
      }
    }
    resetPackFlow();
  };

  const resetPackFlow = () => {
    setCurrentTicket(null);
    setSalesOrder(null);
    setScannedItems([]);
    setPackages([{ id: 1, box: null, weight: '', manualWeight: false }]);
    setActivePackageIndex(0);
    setRates([]);
    setFulfillmentResult(null);
    setLabelData(null);
    setEditingBox(null);
    setShowAddBox(false);
    setError(null);
    setMessage('');
    setMode('queue');
    setIsFbaOrder(false);
    setFbaLabels(null);
    setFbaWeight('40');
    setBatchMode(false);
    setBatchOrders([]);
    setBatchResults(null);
    setBatchProgress({ processed: 0, total: 0, successful: 0, failed: 0 });
    setBatchLastOrder(null);
    setBatchCount(0);
    setBatchErrors([]);
  };

  // FBA complete handler
  const handleFbaComplete = async () => {
    setShipping(true);
    setError(null);
    try {
      const items = salesOrder?.items || currentTicket?.fba_items || [];
      const cartonCount = items.length; // One carton per SKU

      // 1. Fetch FBA labels
      setShippingStep('Fetching labels...');
      try {
        const labelsRes = await axios.get(`/api/packing/fba-labels/${currentTicket.id}`);
        setFbaLabels(labelsRes.data);
      } catch (labelErr) {
        console.warn('FBA labels fetch failed:', labelErr.message);
      }

      // 2. Complete packing
      setShippingStep('Completing...');
      const completePkgs = items.map((item, i) => ({
        boxName: 'FBA Case Pack',
        boxDimensions: '20x13x18',
        weight: parseFloat(fbaWeight) || 40,
      }));

      const completeRes = await axios.post(`/api/packing/complete/${currentTicket.id}`, {
        orderType: 'fba_inbound',
        weight: parseFloat(fbaWeight) || 40,
        boxName: 'FBA Case Pack',
        boxDimensions: '20x13x18',
        packages: completePkgs,
      });

      setFulfillmentResult({
        type: 'fba',
        cartonCount,
        planId: currentTicket.inbound_plan_id || salesOrder?.inboundPlanId,
        totalUnits: items.reduce((s, i) => s + (i.quantity || 0), 0),
      });
      setMode('complete');
      playComplete();
    } catch (err) {
      setError(err.response?.data?.error || 'FBA packing failed');
      playError();
    } finally {
      setShipping(false);
      setShippingStep('');
    }
  };

  // === HELPERS ===

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getCategoryLabel = (cat) => {
    const labels = { 1: 'DTC', 2: 'Wholesale', 3: 'Amazon', 4: 'Faire' };
    return labels[cat] || '';
  };

  const getCategoryClass = (cat) => {
    const classes = { 1: 'dtc', 2: 'wholesale', 3: 'amazon', 4: 'faire' };
    return classes[cat] || '';
  };

  const getTimeSince = (dateStr) => {
    if (!dateStr) return '-';
    const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  const getUpsGroundRate = () => {
    const ground = rates.find(r => r.serviceCode === '03');
    return ground ? parseFloat(ground.cost) : null;
  };

  // Aggregate scanned items for display
  const getAggregatedItems = () => {
    return scannedItems.reduce((acc, item) => {
      const existing = acc.find(i => i.sku === item.sku);
      if (existing) {
        existing.quantity += 1;
      } else {
        acc.push({ ...item, quantity: 1 });
      }
      return acc;
    }, []);
  };

  // === PACKING SLIP HTML BUILDER ===

  const buildPackingSlipHTML = (orderData, items, trackingNum, fulfillResult, boxCount = 1) => {
    const shipTo = orderData?.shipTo || {};
    const totalItems = (items || []).reduce((sum, i) => sum + (i.quantity || 0), 0);

    // Determine brand from SKUs: all CF- prefixed = Candlefish, otherwise Rewined
    const allCF = (items || []).length > 0 && (items || []).every(i => (i.sku || '').toUpperCase().startsWith('CF-'));
    const brandName = allCF ? 'Candlefish' : 'Rewined';
    const logoSrc = allCF ? logoUrls.candlefish : logoUrls.rewined;

    const itemRows = (items || []).map((item, idx) =>
      `<tr class="${idx % 2 === 0 ? 'even' : 'odd'}"><td>${item.sku || ''}</td><td>${item.description || ''}</td><td style="text-align:center">${item.quantity || 0}</td></tr>`
    ).join('');

    const thankYou = allCF
      ? 'Thank you for choosing Candlefish. We hope these scents bring warmth and joy to your space.'
      : 'Thank you for choosing Rewined. We hope you enjoy every moment with your new favorites.';

    const pickupOrDelivery = orderData?.isCustomerPickup
      ? { label: 'Customer Pickup', note: 'Hold for customer — do not ship' }
      : orderData?.isLocalDelivery
      ? { label: 'Local Delivery', note: 'Route with local driver' }
      : null;

    return `<!DOCTYPE html>
<html><head><style>
  @page { margin: 0.5in; }
  body { font-family: 'Georgia', 'Times New Roman', serif; color: #000; margin: 0; padding: 32px 28px; }

  .header { text-align: center; margin-bottom: 32px; }
  .header img { max-width: 220px; max-height: 65px; }
  .header-rule { width: 60px; height: 1px; background: #000; margin: 16px auto; }
  .header h1 { font-size: 10pt; font-weight: 400; letter-spacing: 0.3em; text-transform: uppercase; margin: 0; }

  .info { display: flex; justify-content: space-between; margin-bottom: 32px; gap: 48px; }
  .info-section { flex: 1; }
  .info-label { font-size: 7pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.15em; margin: 0 0 8px; }
  .info-section div { font-size: 9.5pt; line-height: 1.7; }
  .info-section .name { font-weight: 700; font-size: 10pt; }

  table { width: 100%; border-collapse: collapse; margin-bottom: 32px; }
  th { padding: 10px 14px; text-align: left; font-size: 7pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.15em; border-bottom: 1.5pt solid #000; }
  th:last-child { text-align: center; width: 50px; }
  td { padding: 9px 14px; font-size: 9.5pt; border-bottom: 0.5pt solid #ddd; }
  td:first-child { font-family: 'Consolas', 'Courier New', monospace; font-size: 8.5pt; color: #444; }
  td:last-child { text-align: center; font-weight: 600; }
  tr:last-child td { border-bottom: 1.5pt solid #000; }

  .summary { display: flex; justify-content: flex-end; margin-bottom: 40px; font-size: 9pt; }
  .summary-box { text-align: right; }
  .summary-row { display: flex; justify-content: space-between; gap: 24px; }
  .summary-label { text-transform: uppercase; letter-spacing: 0.1em; font-size: 7.5pt; font-weight: 700; }
  .summary-value { font-size: 10pt; }

  .thanks { text-align: center; margin-top: 40px; padding-top: 24px; border-top: 0.5pt solid #ccc; }
  .thanks-title { font-size: 13pt; font-style: italic; margin: 0 0 6px; }
  .thanks-msg { font-size: 8.5pt; color: #555; line-height: 1.6; max-width: 380px; margin: 0 auto; }

  .brand-footer { text-align: center; margin-top: 32px; font-size: 7pt; text-transform: uppercase; letter-spacing: 0.2em; color: #999; }

  .pickup-banner { margin: 0 0 24px; padding: 12px 16px; border: 2pt solid #000; text-align: center; }
  .pickup-banner-label { font-size: 12pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.25em; margin: 0 0 4px; }
  .pickup-banner-note { font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.15em; color: #444; margin: 0; }
</style></head><body>
  <div class="header">
    ${logoSrc ? `<img src="${logoSrc}" alt="${brandName}" />` : `<div style="font-size:18pt;font-weight:700;letter-spacing:0.1em">${brandName.toUpperCase()}</div>`}
    <div class="header-rule"></div>
    <h1>Packing List</h1>
  </div>

  ${pickupOrDelivery ? `<div class="pickup-banner"><p class="pickup-banner-label">${pickupOrDelivery.label}</p><p class="pickup-banner-note">${pickupOrDelivery.note}</p></div>` : ''}

  <div class="info">
    <div class="info-section">
      <div class="info-label">Ship To</div>
      <div class="name">${shipTo.name || orderData?.customerName || ''}</div>
      ${shipTo.addr1 ? `<div>${shipTo.addr1}</div>` : ''}
      ${shipTo.addr2 ? `<div>${shipTo.addr2}</div>` : ''}
      ${shipTo.city ? `<div>${shipTo.city}, ${shipTo.state || ''} ${shipTo.zip || ''}</div>` : ''}
      ${shipTo.country && shipTo.country !== 'US' ? `<div>${shipTo.country}</div>` : ''}
    </div>
    <div class="info-section" style="text-align:right">
      <div class="info-label">Order Details</div>
      <div>Order <strong>${orderData?.orderNumber || ''}</strong></div>
      <div>${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
      ${trackingNum ? `<div style="margin-top:4px;font-family:Consolas,monospace;font-size:8.5pt;color:#444">${trackingNum}</div>` : ''}
    </div>
  </div>

  <table>
    <thead><tr><th>SKU</th><th>Description</th><th>Qty</th></tr></thead>
    <tbody>${itemRows}</tbody>
  </table>

  <div class="summary">
    <div class="summary-box">
      <div class="summary-row">
        <span class="summary-label">Total Items</span>
        <span class="summary-value">${totalItems}</span>
      </div>
      ${boxCount > 1 ? `<div class="summary-row" style="margin-top:4px">
        <span class="summary-label">Boxes</span>
        <span class="summary-value">${boxCount}</span>
      </div>` : ''}
    </div>
  </div>

  <div class="thanks">
    <div class="thanks-title">Thank You for Your Order</div>
    <div class="thanks-msg">${thankYou}</div>
  </div>

  <div class="brand-footer">${brandName} &mdash; Charleston, SC</div>
</body></html>`;
  };

  // === RECENT SHIPMENTS ===

  const fetchRecentShipments = async (page = 1, search = '') => {
    setLoadingRecent(true);
    try {
      const params = new URLSearchParams({ page, limit: recentLimit });
      if (search) params.set('search', search);
      const res = await axios.get(`/api/packing/recent?${params}`);
      setRecentShipments(res.data.shipments || []);
      setRecentTotal(res.data.total || 0);
      setRecentPage(res.data.page || 1);
    } catch (err) {
      console.error('Failed to fetch recent shipments:', err);
    } finally {
      setLoadingRecent(false);
    }
  };

  const handleToggleRecent = () => {
    const next = !showRecentShipments;
    setShowRecentShipments(next);
    if (next) {
      setRecentSearch('');
      setRecentPage(1);
      fetchRecentShipments(1, '');
      fetchStuckTickets();
    }
  };

  const handleRecentSearch = (e) => {
    e.preventDefault();
    setRecentPage(1);
    fetchRecentShipments(1, recentSearch);
  };

  const handleRecentPageChange = (newPage) => {
    setRecentPage(newPage);
    fetchRecentShipments(newPage, recentSearch);
  };

  const handleCopyTracking = (id, tracking) => {
    navigator.clipboard.writeText(tracking);
    setCopiedTrackingId(id);
    setTimeout(() => setCopiedTrackingId(null), 1500);
  };

  const handleRegenerateLabel = async (shipment) => {
    if (!window.confirm(`Regenerate UPS label(s) for ${shipment.tran_id}?\n\nThis will:\n• VOID any existing labels (${(shipment.all_tracking_numbers || []).length} known)\n• Buy fresh label(s) at UPS\n• Attach new tracking to existing NetSuite fulfillment\n• Print the new label\n\nUse this when the original label was lost (e.g. silent ECONNRESET during pack).`)) {
      return;
    }
    setRegeneratingId(shipment.id);
    try {
      const res = await axios.post(`/api/packing/regenerate-label/${shipment.id}`, {
        voidOldLabels: true
      });
      if (!res.data?.success) {
        setError(`Regenerate failed: ${res.data?.error || 'unknown'}`);
        return;
      }
      const newPkgs = res.data.packages || [];
      const newCost = res.data.totalCost || 0;
      const voidedCount = res.data.voidedCount || 0;
      const nsPushed = res.data.trackingPushedToNetSuite;
      setMessage(`Regenerated ${newPkgs.length} label(s), voided ${voidedCount} old, total $${newCost.toFixed(2)}${nsPushed ? '' : ' (NS tracking push failed - update manually)'}`);

      // Print all new labels
      let printed = false;
      const savedIp = localStorage.getItem('zebraPrinterIp');
      if (savedIp) {
        try {
          await axios.post(`/api/packing/print-label/${shipment.id}`, { printerIp: savedIp });
          printed = true;
        } catch (netErr) {
          console.log('Network print failed, trying QZ:', netErr.message);
        }
      }
      if (!printed && qzConnected) {
        for (const pkg of newPkgs) {
          if (pkg.labelData) {
            try {
              await qzPrint.printZPL(atob(pkg.labelData), qzSelectedPrinter || null);
            } catch (qzErr) { console.error('QZ print failed:', qzErr.message); }
          }
        }
      }
      // Refresh recent + stuck lists
      fetchRecentShipments(recentPage, recentSearch);
      fetchStuckTickets();
    } catch (err) {
      setError(`Regenerate failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setRegeneratingId(null);
    }
  };

  const fetchStuckTickets = async () => {
    try {
      const res = await axios.get('/api/packing/stuck?minStuckMinutes=15&maxAgeDays=7');
      setStuckTickets(res.data?.stuck || []);
    } catch (err) {
      console.error('Failed to fetch stuck tickets:', err);
    }
  };

  const handleReprintLabel = async (shipment) => {
    try {
      // If label_data is missing (e.g. recovered ticket from ECONNRESET event),
      // offer to regenerate fresh labels instead of just failing.
      if (!shipment.has_label) {
        if (window.confirm(`No saved label data for ${shipment.tran_id}.\n\nRegenerate a fresh UPS label now? (This voids any old labels and buys a new one.)`)) {
          return handleRegenerateLabel(shipment);
        }
        return;
      }

      // Fetch label data from server (returns {labelData, packages: [...]})
      const res = await axios.get(`/api/packing/label/${shipment.id}`);
      const packages = (res.data?.packages || []).filter(p => p.labelData);
      const primaryLabel = res.data?.labelData || packages[0]?.labelData || null;
      if (!primaryLabel) {
        setError('No label data stored for this shipment');
        return;
      }

      // Try server-side network print first — handles multi-box by iterating
      const savedIp = localStorage.getItem('zebraPrinterIp');
      if (savedIp) {
        try {
          await axios.post(`/api/packing/print-label/${shipment.id}`, { printerIp: savedIp });
          setMessage(`Label reprinted to Zebra${packages.length > 1 ? ` (${packages.length} boxes)` : ''}`);
          return;
        } catch (netErr) {
          console.log('Network print failed, trying QZ:', netErr.message);
        }
      }

      const labelsToPrint = packages.length > 0 ? packages.map(p => p.labelData) : [primaryLabel];

      // Try QZ Tray — print every package that has label data, not just the first
      if (qzConnected) {
        try {
          for (const labelData of labelsToPrint) {
            await qzPrint.printZPL(atob(labelData), qzSelectedPrinter || null);
          }
          setMessage(`Label reprinted via QZ Tray${labelsToPrint.length > 1 ? ` (${labelsToPrint.length} boxes)` : ''}`);
          return;
        } catch (qzErr) {
          console.log('QZ print failed, trying TCP:', qzErr.message);
        }
      }

      // Fallback: server-side TCP print with raw ZPL
      if (savedIp) {
        try {
          for (const labelData of labelsToPrint) {
            await axios.post('/api/print/zpl', { zpl: atob(labelData), printerIp: savedIp });
          }
          setMessage(`Label reprinted to Zebra${labelsToPrint.length > 1 ? ` (${labelsToPrint.length} boxes)` : ''}`);
          return;
        } catch (netErr) {
          console.log('TCP print failed:', netErr.message);
        }
      }

      // Last resort: download .zpl files
      labelsToPrint.forEach((ld, i) => {
        const blob = new Blob([atob(ld)], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const tracking = packages[i]?.trackingNumber || shipment.tran_id || 'shipping';
        a.download = `label-${tracking}${labelsToPrint.length > 1 ? `-box${i + 1}` : ''}.zpl`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
      setMessage(`No printer connected — label${labelsToPrint.length > 1 ? 's' : ''} downloaded`);
    } catch (err) {
      setError(`Reprint failed: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleReprintSlip = async (shipment) => {
    if (!shipment.packed_items || shipment.packed_items.length === 0) {
      setError('No packed items data stored for this shipment');
      return;
    }

    const html = buildPackingSlipHTML(
      { orderNumber: shipment.tran_id, customerName: shipment.customer_name, shipTo: {} },
      shipment.packed_items,
      shipment.tracking_number,
      null
    );

    // Try QZ Tray laser printer
    if (qzConnected && laserPrinter) {
      try {
        await qzPrint.printHTML(html, laserPrinter);
        setMessage('Packing slip reprinted to laser');
        return;
      } catch (err) {
        console.error('Laser reprint failed:', err.message);
      }
    }

    // Fallback: open in new window and print
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };

  const handleVoidShipment = async (shipment) => {
    if (!window.confirm(`Void shipment for ${shipment.tran_id}?\n\nTracking: ${shipment.tracking_number || 'N/A'}\n\nThis will attempt to void the UPS label. NetSuite fulfillment must be reversed manually if needed.`)) {
      return;
    }

    setVoidingId(shipment.id);
    try {
      const res = await axios.post(`/api/packing/void/${shipment.id}`);
      if (res.data.voidSuccess) {
        setMessage(`Shipment voided successfully`);
      } else {
        setMessage(`Ticket marked voided. UPS void may have failed - check manually.`);
      }
      fetchRecentShipments();
    } catch (err) {
      setError(`Void failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setVoidingId(null);
    }
  };

  const handleVoidAndReship = async (shipment) => {
    if (!window.confirm(
      `Void & reship ${shipment.tran_id}?\n\nTracking: ${shipment.tracking_number || 'N/A'}\n\n` +
      `Only do this if the package has NOT been picked up by the carrier yet — ` +
      `UPS can't void a label once it's been scanned into their network, and this will fail loudly if so.\n\n` +
      `This voids the label(s), reverses the NetSuite fulfillment, and puts the order back in the pick queue immediately.`
    )) {
      return;
    }

    setReshippingId(shipment.id);
    try {
      const res = await axios.post(`/api/packing/void-and-reship/${shipment.id}`);
      setMessage(res.data.message || 'Voided and reopened for reship');
      fetchRecentShipments();
    } catch (err) {
      setError(`Void & reship failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setReshippingId(null);
    }
  };

  // Void or edit the order that was *just* packed, from the "Order Shipped"
  // screen — covers a packer noticing right away that a box was forgotten
  // (Edit Order) or that the order shouldn't have shipped at all (Void Order).
  const handleVoidJustShippedOrder = async () => {
    if (!currentTicket?.id) return;
    const trackingNumbers = (labelData?.packages || [labelData])
      .map(lp => lp?.trackingNumber).filter(Boolean);
    if (!window.confirm(
      `Void this shipment for ${currentTicket.tran_id}?\n\nTracking: ${trackingNumbers.join(', ') || 'N/A'}\n\n` +
      `This voids the UPS label(s). Use this if the order wasn't supposed to ship — ` +
      `NetSuite fulfillment must be reversed manually if needed.`
    )) {
      return;
    }
    setVoidingId(currentTicket.id);
    try {
      const res = await axios.post(`/api/packing/void/${currentTicket.id}`);
      if (res.data.voidSuccess) {
        resetPackFlow();
        setMessage(`${currentTicket.tran_id} voided.`);
      } else {
        const reason = (res.data.voidResults || []).filter(r => !r.success).map(r => r.error).join('; ');
        resetPackFlow();
        setError(`${currentTicket.tran_id} marked voided, but the UPS label could not be voided — check manually. ${reason}`);
      }
    } catch (err) {
      setError(`Void failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setVoidingId(null);
    }
  };

  const handleEditJustShippedOrder = async () => {
    if (!currentTicket?.id) return;
    const trackingNumbers = (labelData?.packages || [labelData])
      .map(lp => lp?.trackingNumber).filter(Boolean);
    if (!window.confirm(
      `Edit ${currentTicket.tran_id}?\n\nTracking: ${trackingNumbers.join(', ') || 'N/A'}\n\n` +
      `Only do this if the package has NOT been picked up by the carrier yet — ` +
      `UPS can't void a label once it's been scanned into their network, and this will fail loudly if so.\n\n` +
      `This voids the label(s), reverses the NetSuite fulfillment, and puts the order back in the pick queue so you can repack it (e.g. add a forgotten box).`
    )) {
      return;
    }
    setReshippingId(currentTicket.id);
    try {
      const res = await axios.post(`/api/packing/void-and-reship/${currentTicket.id}`);
      const msg = res.data.message || `${currentTicket.tran_id} reopened for edit.`;
      resetPackFlow();
      setMessage(msg);
    } catch (err) {
      setError(`Edit failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setReshippingId(null);
    }
  };

  // === RENDER ===

  // Welcome screen
  if (showWelcome && currentOperator) {
    return (
      <div className="welcome-screen">
        <div className="welcome-content">
          <h1 className="welcome-greeting">{getGreeting()},</h1>
          <h2 className="welcome-name">{currentOperator.first_name}</h2>
        </div>
      </div>
    );
  }

  // Goodbye screen
  if (showGoodbye) {
    return (
      <div className="welcome-screen goodbye-screen">
        <div className="welcome-content">
          <h1 className="welcome-greeting">Catch you later,</h1>
          <h2 className="welcome-name">{goodbyeName}</h2>
        </div>
      </div>
    );
  }

  // PIN login
  if (!currentOperator) {
    return (
      <div className="department-screen operator-login-screen">
        <div className="login-prompt-container">
          <h1 className="login-title">Pack Station</h1>
          <div className="pin-entry-box">
            <p className="login-instruction">Enter your PIN</p>
            <div className="pin-display">
              {[0, 1, 2, 3].map(i => (
                <div key={i} className={`pin-dot ${i < pinInput.length ? 'filled' : ''}`} />
              ))}
            </div>
            {pinError && <p className="pin-error">{pinError}</p>}
            <div className="pin-keypad">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
                <button key={d} className="pin-key" onClick={() => handlePinDigit(d.toString())}>{d}</button>
              ))}
              <button className="pin-key pin-clear" onClick={handlePinClear}>Clear</button>
              <button className="pin-key" onClick={() => handlePinDigit('0')}>0</button>
              <div className="pin-key pin-spacer" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Boxing mode - inline flow with multi-box support
  // FBA Boxing Mode
  if (mode === 'boxing' && isFbaOrder) {
    const items = salesOrder?.items || currentTicket?.fba_items || [];
    const totalUnits = items.reduce((s, i) => s + (i.quantity || 0), 0);
    const cartonCount = items.length;

    return (
      <div className="pack-station">
        <div className="pack-topbar">
          <div className="pack-topbar-left">
            <button className="pack-back-btn" onClick={resetPackFlow}>Back</button>
            <span className="pack-topbar-order">{currentTicket?.tran_id}</span>
            <span className="picking-category-badge fba-inbound" style={{ marginLeft: 12 }}>FBA INBOUND</span>
          </div>
          <div className="pack-topbar-right">
            <span className="pack-topbar-operator">{currentOperator?.display_name || currentOperator?.first_name}</span>
          </div>
        </div>

        <div className="pack-content" style={{ padding: '24px', maxWidth: 800, margin: '0 auto' }}>
          <h2 style={{ margin: '0 0 24px', color: '#283593' }}>FBA Inbound Packing</h2>

          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
            <div style={{ background: '#f5f5f5', borderRadius: 8, padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{cartonCount}</div>
              <div style={{ fontSize: 13, color: '#666' }}>Cartons</div>
            </div>
            <div style={{ background: '#f5f5f5', borderRadius: 8, padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{totalUnits}</div>
              <div style={{ fontSize: 13, color: '#666' }}>Total Units</div>
            </div>
            <div style={{ background: '#f5f5f5', borderRadius: 8, padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700 }}>20x13x18</div>
              <div style={{ fontSize: 13, color: '#666' }}>Box Size (in)</div>
            </div>
          </div>

          {/* Items table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24, fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                <th style={{ padding: '8px 12px' }}>SKU</th>
                <th style={{ padding: '8px 12px' }}>FNSKU</th>
                <th style={{ padding: '8px 12px' }}>Product</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Qty</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{item.seller_sku || item.sku}</td>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#283593' }}>{item.fnsku || '-'}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13, color: '#555' }}>{(item.product_name || item.description || '').substring(0, 40)}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{item.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Weight per carton */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <label style={{ fontWeight: 600 }}>Weight per carton (lbs):</label>
            <input
              type="number"
              value={fbaWeight}
              onChange={(e) => setFbaWeight(e.target.value)}
              style={{ width: 80, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 16, textAlign: 'center' }}
            />
          </div>

          {/* Label buttons */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            <button
              style={{ flex: 1, padding: '12px 20px', background: '#e8eaf6', color: '#283593', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
              onClick={async () => {
                try {
                  setMessage('Fetching FNSKU labels...');
                  const res = await axios.get(`/api/packing/fba-labels/${currentTicket.id}`);
                  setFbaLabels(res.data);
                  if (res.data.fnskuPdf) {
                    if (qzConnected && laserPrinter) {
                      await qzPrint.printPdf(res.data.fnskuPdf, laserPrinter);
                      setMessage('FNSKU labels sent to ' + laserPrinter);
                    } else {
                      const blob = new Blob([Uint8Array.from(atob(res.data.fnskuPdf), c => c.charCodeAt(0))], { type: 'application/pdf' });
                      window.open(URL.createObjectURL(blob), '_blank');
                      setMessage('FNSKU labels opened for printing (no laser printer configured)');
                    }
                  }
                } catch (err) {
                  setError('Failed to print FNSKU labels: ' + (err.message || ''));
                }
              }}
            >
              Print FNSKU Labels
            </button>
            <button
              style={{ flex: 1, padding: '12px 20px', background: '#fff8e1', color: '#f57f17', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
              onClick={async () => {
                try {
                  setMessage('Fetching carton labels...');
                  const res = await axios.get(`/api/packing/fba-labels/${currentTicket.id}`);
                  if (res.data.cartonLabels?.length > 0) {
                    window.open(res.data.cartonLabels[0].downloadUrl, '_blank');
                    setMessage('Carton labels opened for printing');
                  } else {
                    setError('No carton labels available yet');
                  }
                } catch (err) {
                  setError('Failed to fetch carton labels');
                }
              }}
            >
              Print Carton Labels
            </button>
          </div>

          {error && <div style={{ color: '#c62828', marginBottom: 16, padding: '8px 12px', background: '#ffebee', borderRadius: 6 }}>{error}</div>}
          {message && <div style={{ color: '#2e7d32', marginBottom: 16, padding: '8px 12px', background: '#e8f5e9', borderRadius: 6 }}>{message}</div>}

          {/* Complete button */}
          <button
            onClick={handleFbaComplete}
            disabled={shipping}
            style={{
              width: '100%', padding: '16px 24px', background: shipping ? '#90a4ae' : '#283593', color: 'white',
              border: 'none', borderRadius: 8, fontSize: 18, fontWeight: 700, cursor: shipping ? 'default' : 'pointer'
            }}
          >
            {shipping ? shippingStep || 'Processing...' : `Complete FBA Packing (${cartonCount} cartons)`}
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'boxing') {
    const soShippingCost = salesOrder?.shippingCost;
    const upsRate = getUpsGroundRate();
    const costDiff = (soShippingCost != null && upsRate != null) ? soShippingCost - upsRate : null;
    const activePkg = packages[activePackageIndex];

    return (
      <div className="pack-station">
        {/* Sticky header bar */}
        <div className="pack-topbar">
          <span className="pack-topbar-title">
            {batchMode ? `Batch Ship: ${batchOrders.length} orders` : `Box & Ship: ${currentTicket?.tran_id}`}
          </span>
          <div className="pack-topbar-user">
            <span className={`pack-printer-status ${qzConnected ? 'connected' : 'disconnected'}`}>
              {qzConnected ? '\u25CF' : '\u25CB'} {qzSelectedPrinter ? qzSelectedPrinter.split(' ')[0] : 'Printer'}
            </span>
            <button className="pack-cancel-btn" onClick={() => {
              if (batchMode && !batchProcessing) {
                setBatchMode(false);
                setMode('items');
                setMessage('Batch mode cancelled');
              } else if (!batchProcessing) {
                handleBackToScanning();
              }
            }} disabled={batchProcessing}>
              {batchProcessing ? 'Processing...' : 'Back'}
            </button>
          </div>
        </div>

        <div className="pack-dashboard" onClick={resetActivity}>
          {/* Customer info */}
          <div className="pack-context-bar">
            <span className="pack-context-customer">{salesOrder?.customerName || currentTicket?.customer_name}</span>
          </div>

          {(error || message) && (
            <div className={`pack-message ${error ? 'error' : 'success'}`}>
              {error || message}
            </div>
          )}

          {/* Ship-To Address */}
          {salesOrder?.shipTo && salesOrder.shipTo.addr1 && (
            <div className="pack-ship-to">
              <span className="pack-ship-to-label">Ship To:</span>
              <span className="pack-ship-to-address">
                {salesOrder.shipTo.name}, {salesOrder.shipTo.addr1}{salesOrder.shipTo.addr2 ? `, ${salesOrder.shipTo.addr2}` : ''}, {salesOrder.shipTo.city}, {salesOrder.shipTo.state} {salesOrder.shipTo.zip}
              </span>
            </div>
          )}

          {/* Address check — shows only when there is something to act on:
              a NetSuite record we had to repair, a field UPS filled in, a
              carrier correction, or a field nobody can supply. The packer
              decides; nothing here blocks the pack flow. */}
          {addressCheck && !isLocalOrPickup && (() => {
            const fixed = addressCheck.filledFromCarrier?.length || addressCheck.repaired;
            const blocked = addressCheck.remainingProblems?.length > 0;
            const sugg = addressCheck.suggestion;
            if (!fixed && !blocked && !sugg) return null;
            const fmt = (a) => a && `${a.address1}${a.address2 ? `, ${a.address2}` : ''}, ${a.city}, ${a.state} ${a.zip}`;
            const tone = blocked
              ? { bg: '#ffebee', border: '#c62828', text: '#b71c1c' }
              : { bg: '#fff8e1', border: '#8B6F4E', text: '#5d4a33' };
            return (
              <div style={{ background: tone.bg, border: `2px solid ${tone.border}`, borderRadius: '6px', padding: '10px 14px', margin: '8px 0', fontSize: '0.95rem' }}>
                {blocked && (
                  <div style={{ color: tone.text, fontWeight: 600 }}>
                    ADDRESS INCOMPLETE — NetSuite is missing {addressCheck.remainingProblems.join(' and ')}.
                    UPS could not supply it either. Fix the address in NetSuite before shipping.
                  </div>
                )}
                {!blocked && fixed && (
                  <div style={{ color: tone.text }}>
                    <strong>Address repaired.</strong>{' '}
                    {addressCheck.filledFromCarrier?.length
                      ? `UPS supplied the ${addressCheck.filledFromCarrier.join(' and ')}. `
                      : 'The NetSuite record had its fields shifted. '}
                    Shipping to: {fmt(acceptedAddress || addressCheck.effective)}
                  </div>
                )}
                {sugg && !acceptedAddress && (
                  <div style={{ marginTop: fixed || blocked ? '8px' : 0, color: tone.text }}>
                    <div>
                      <strong>UPS suggests</strong> ({addressCheck.suggestionChanges.join(', ')} differ): {fmt(sugg)}
                    </div>
                    <div style={{ marginTop: '6px', display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => { setAcceptedAddress(sugg); setMessage('Using the UPS-corrected address for this label.'); }}
                        style={{ background: '#8B6F4E', color: '#fff', border: 'none', borderRadius: '4px', padding: '6px 12px', cursor: 'pointer' }}
                      >
                        Use UPS address
                      </button>
                      <button
                        onClick={() => setAddressCheck({ ...addressCheck, suggestion: null })}
                        style={{ background: '#fff', color: tone.text, border: `1px solid ${tone.border}`, borderRadius: '4px', padding: '6px 12px', cursor: 'pointer' }}
                      >
                        Keep NetSuite address
                      </button>
                    </div>
                  </div>
                )}
                {acceptedAddress && (
                  <div style={{ marginTop: '8px', color: tone.text }}>
                    Using the UPS address for this label.{' '}
                    <button
                      onClick={() => setAcceptedAddress(null)}
                      style={{ background: 'none', border: 'none', color: tone.text, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
                    >
                      undo
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Expedited banner — the label purchase honors the SO's ship method
              server-side; this makes sure the packer notices too. */}
          {!isLocalOrPickup && /next day|2nd day|3 day select/i.test(salesOrder?.shipMethodName || '') && (
            <div style={{ background: '#fff3e0', border: '2px solid #e65100', borderRadius: '6px', padding: '10px 14px', margin: '8px 0', fontSize: '0.95rem' }}>
              <strong style={{ color: '#e65100' }}>EXPEDITED: {salesOrder.shipMethodName}</strong>
              <span style={{ marginLeft: '10px', color: '#bf360c' }}>
                — ship this order TODAY. The label will print as {salesOrder.shipMethodName}, not Ground.
              </span>
            </div>
          )}

          {/* Local delivery / customer pickup banner — prevents accidentally buying UPS labels for these. */}
          {isLocalOrPickup && (
            <div style={{ background: '#e8f5e9', border: '2px solid #2e7d32', borderRadius: '6px', padding: '10px 14px', margin: '8px 0', fontSize: '0.95rem' }}>
              <strong style={{ color: '#1b5e20' }}>
                {salesOrder?.shipMethodName || localOrPickupLabel}
              </strong>
              <span style={{ marginLeft: '10px', color: '#1b5e20' }}>
                — NetSuite ship method is {localOrPickupLabel}. Ship It will complete the fulfillment <strong>without printing a UPS label</strong>.
              </span>
            </div>
          )}

          {/* Package Tabs (hidden in batch mode) */}
          {packages.length > 1 && !batchMode && (
            <div className="pack-package-tabs">
              {packages.map((pkg, i) => (
                <button
                  key={pkg.id}
                  className={`pack-package-tab ${i === activePackageIndex ? 'active' : ''} ${pkg.box && pkg.weight ? 'ready' : ''}`}
                  onClick={() => setActivePackageIndex(i)}
                >
                  <span className="pack-package-tab-label">Box {i + 1}</span>
                  {pkg.box && pkg.weight && <span className="pack-package-tab-check">&#10003;</span>}
                  {packages.length > 1 && (
                    <span
                      className="pack-package-tab-remove"
                      onClick={e => { e.stopPropagation(); removePackage(i); }}
                      title="Remove this box"
                    >
                      &times;
                    </span>
                  )}
                </button>
              ))}
              <button className="pack-package-tab pack-package-tab-add" onClick={addPackage}>
                + Add Box
              </button>
            </div>
          )}

          {/* Active Package Info (summary for multi-box) */}
          {packages.length > 1 && (
            <div className="pack-package-summary">
              {packages.map((pkg, i) => (
                <div key={pkg.id} className={`pack-package-chip ${i === activePackageIndex ? 'active' : ''} ${pkg.box && pkg.weight ? 'ready' : ''}`}>
                  <strong>Box {i + 1}:</strong> {pkg.box ? pkg.box.name : 'No box'}{pkg.weight ? `, ${pkg.weight} lbs` : ''}
                </div>
              ))}
            </div>
          )}

          {/* Box Selection Grid - for active package */}
          <div className="pack-box-section">
            <h3 className="pack-section-title">
              {packages.length > 1 ? `Select Box for Box ${activePackageIndex + 1}` : 'Select Box'}
            </h3>
            <div className="pack-box-grid">
              {boxTypes.map(box => (
                <div
                  key={box.id}
                  className={`pack-box-option ${activePkg?.box?.id === box.id ? 'selected' : ''}`}
                  onClick={() => { setSelectedBox(box); setEditingBox(null); }}
                >
                  {editingBox === box.id ? (
                    <div className="pack-edit-box-form" onClick={e => e.stopPropagation()}>
                      <div className="pack-edit-dims">
                        <input type="number" value={editForm.length} onChange={e => setEditForm({...editForm, length: e.target.value})} placeholder="L" />
                        <span>x</span>
                        <input type="number" value={editForm.width} onChange={e => setEditForm({...editForm, width: e.target.value})} placeholder="W" />
                        <span>x</span>
                        <input type="number" value={editForm.height} onChange={e => setEditForm({...editForm, height: e.target.value})} placeholder="H" />
                      </div>
                      <div className="pack-edit-actions">
                        <button className="pack-edit-save" onClick={handleSaveBoxEdit}>Save</button>
                        <button className="pack-edit-cancel" onClick={() => setEditingBox(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="pack-box-name">{box.name}</div>
                      <div className="pack-box-dims">{box.length}x{box.width}x{box.height}</div>
                      <button
                        className="pack-box-edit-btn"
                        onClick={e => { e.stopPropagation(); handleEditBox(box); }}
                      >
                        Edit
                      </button>
                    </>
                  )}
                </div>
              ))}

              {/* Add New Box Type */}
              {showAddBox ? (
                <div className="pack-box-option pack-new-box-form">
                  <input
                    type="text"
                    placeholder="Box name"
                    value={newBoxForm.name}
                    onChange={e => setNewBoxForm({...newBoxForm, name: e.target.value})}
                    className="pack-new-box-name"
                  />
                  <div className="pack-edit-dims">
                    <input type="number" placeholder="L" value={newBoxForm.length} onChange={e => setNewBoxForm({...newBoxForm, length: e.target.value})} />
                    <span>x</span>
                    <input type="number" placeholder="W" value={newBoxForm.width} onChange={e => setNewBoxForm({...newBoxForm, width: e.target.value})} />
                    <span>x</span>
                    <input type="number" placeholder="H" value={newBoxForm.height} onChange={e => setNewBoxForm({...newBoxForm, height: e.target.value})} />
                  </div>
                  <div className="pack-edit-actions">
                    <button className="pack-edit-save" onClick={handleAddNewBox}>Add</button>
                    <button className="pack-edit-cancel" onClick={() => { setShowAddBox(false); setNewBoxForm({ name: '', length: '', width: '', height: '' }); }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="pack-box-option pack-add-box" onClick={() => setShowAddBox(true)}>
                  <div className="pack-box-name">+ Add Type</div>
                </div>
              )}
            </div>
          </div>

          {/* Weight Section - for active package */}
          <div className="pack-weight-section">
            <h3 className="pack-section-title">
              {packages.length > 1 ? `Weight for Box ${activePackageIndex + 1}` : 'Weight'}
            </h3>
            <div className="pack-weight-controls">
              {!manualWeight ? (
                <>
                  <button
                    className="pack-weight-capture-btn"
                    onClick={captureWeight}
                    disabled={isCapturingWeight}
                  >
                    {isCapturingWeight ? 'Reading Scale...' : 'Capture from Scale'}
                  </button>
                  <button
                    className="pack-weight-manual-btn"
                    onClick={() => setManualWeight(true)}
                  >
                    Manual Entry
                  </button>
                  {weight && (
                    <div className="pack-weight-display">
                      <span className="pack-weight-value">{weight}</span>
                      <span className="pack-weight-unit">lbs</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="pack-weight-manual-form">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Enter weight"
                    value={weight}
                    onChange={e => setWeight(e.target.value)}
                    className="pack-weight-input"
                    autoFocus
                  />
                  <span className="pack-weight-unit">lbs</span>
                  <button
                    className="pack-weight-confirm"
                    onClick={() => {
                      if (weight && parseFloat(weight) > 0) {
                        setManualWeight(false);
                        setMessage(`Box ${activePackageIndex + 1} weight: ${weight} lbs`);
                      }
                    }}
                    disabled={!weight || parseFloat(weight) <= 0}
                  >
                    Set
                  </button>
                  <button className="pack-weight-cancel" onClick={() => { setManualWeight(false); setWeight(''); }}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Add Another Box button (for single-box mode, hidden in batch mode) */}
          {packages.length === 1 && !batchMode && (
            <div style={{ marginBottom: 12 }}>
              <button className="pack-incomplete-button" onClick={addPackage}>
                + Add Another Box
              </button>
            </div>
          )}

          {/* Cost Comparison Bar (hidden in batch mode) */}
          {!batchMode && (soShippingCost != null || upsRate != null) && (
            <div className="pack-cost-bar">
              <div className="pack-cost-item">
                <span className="pack-cost-label">SO Charged</span>
                <span className="pack-cost-value">
                  {soShippingCost != null ? `$${soShippingCost.toFixed(2)}` : '--'}
                </span>
              </div>
              <div className="pack-cost-item">
                <span className="pack-cost-label">UPS Estimate</span>
                <span className="pack-cost-value">
                  {loadingRates ? 'Loading...' : upsRate != null ? `$${upsRate.toFixed(2)}` : '--'}
                </span>
              </div>
              {costDiff != null && (
                <div className={`pack-cost-diff ${costDiff >= 0 ? 'positive' : 'negative'}`}>
                  {costDiff >= 0 ? `+$${costDiff.toFixed(2)} margin` : `-$${Math.abs(costDiff).toFixed(2)} loss`}
                </div>
              )}
            </div>
          )}

          {/* Batch Quantity Selector */}
          {batchMode && !batchProcessing && !batchResults && (
            <div className="pack-batch-qty-section">
              <h3 className="pack-section-title">How many to ship?</h3>
              <div className="pack-batch-qty-controls">
                <button
                  className="pack-batch-qty-btn"
                  onClick={() => setBatchCount(Math.max(1, batchCount - 10))}
                >
                  -10
                </button>
                <button
                  className="pack-batch-qty-btn"
                  onClick={() => setBatchCount(Math.max(1, batchCount - 1))}
                >
                  -
                </button>
                <input
                  type="number"
                  className="pack-batch-qty-input"
                  value={batchCount}
                  onChange={e => {
                    const val = Math.max(1, Math.min(batchOrders.length, parseInt(e.target.value) || 1));
                    setBatchCount(val);
                  }}
                  min={1}
                  max={batchOrders.length}
                />
                <span className="pack-batch-qty-of">of {batchOrders.length}</span>
                <button
                  className="pack-batch-qty-btn"
                  onClick={() => setBatchCount(Math.min(batchOrders.length, batchCount + 1))}
                >
                  +
                </button>
                <button
                  className="pack-batch-qty-btn"
                  onClick={() => setBatchCount(Math.min(batchOrders.length, batchCount + 10))}
                >
                  +10
                </button>
                <button
                  className="pack-batch-qty-all"
                  onClick={() => setBatchCount(batchOrders.length)}
                >
                  All
                </button>
              </div>
            </div>
          )}

          {/* Batch Progress */}
          {batchProcessing && (
            <div className="pack-batch-progress">
              <div className="pack-batch-progress-header">
                <span>Processing batch: {batchProgress.processed} / {batchProgress.total}</span>
                <span className="pack-batch-progress-pct">
                  {batchProgress.total > 0 ? Math.round((batchProgress.processed / batchProgress.total) * 100) : 0}%
                </span>
              </div>
              <div className="pack-batch-progress-bar">
                <div
                  className="pack-batch-progress-fill"
                  style={{ width: `${batchProgress.total > 0 ? (batchProgress.processed / batchProgress.total) * 100 : 0}%` }}
                />
              </div>
              <div className="pack-batch-progress-stats">
                <span className="pack-batch-stat-success">{batchProgress.successful} shipped</span>
                {batchProgress.failed > 0 && (
                  <span className="pack-batch-stat-fail">{batchProgress.failed} failed</span>
                )}
              </div>
              {batchLastOrder && (
                <div className="pack-batch-last-order">
                  Last: {batchLastOrder.orderNumber} &rarr; {batchLastOrder.trackingNumber}
                </div>
              )}
            </div>
          )}

          {/* Batch Complete */}
          {batchResults && (
            <div className="pack-batch-complete">
              <h2>Batch Complete</h2>
              <div className="pack-batch-complete-stats">
                <div className="pack-batch-complete-stat">
                  <span className="pack-batch-complete-value">{batchResults.successful}</span>
                  <span className="pack-batch-complete-label">Shipped</span>
                </div>
                {batchResults.failed > 0 && (
                  <div className="pack-batch-complete-stat fail">
                    <span className="pack-batch-complete-value">{batchResults.failed}</span>
                    <span className="pack-batch-complete-label">Failed</span>
                  </div>
                )}
                <div className="pack-batch-complete-stat">
                  <span className="pack-batch-complete-value">${batchResults.totalCost}</span>
                  <span className="pack-batch-complete-label">Label Cost</span>
                </div>
              </div>
              {batchErrors.length > 0 && (
                <div className="pack-batch-errors">
                  <h3>Errors ({batchErrors.length})</h3>
                  <div className="pack-batch-error-list">
                    {batchErrors.slice(0, 10).map((e, i) => (
                      <div key={i} className="pack-batch-error-item">
                        <span className="pack-batch-error-order">{e.orderNumber}</span>
                        <span className="pack-batch-error-msg">{e.error}</span>
                      </div>
                    ))}
                    {batchErrors.length > 10 && (
                      <div className="pack-batch-error-more">...and {batchErrors.length - 10} more</div>
                    )}
                  </div>
                </div>
              )}
              <button className="pack-ship-btn" onClick={handleBatchDone}>
                Done
              </button>
            </div>
          )}

          {/* Ship Button */}
          {!batchResults && (
            <div className="pack-ship-section">
              {batchMode ? (
                <button
                  className="pack-ship-btn pack-batch-ship-btn"
                  onClick={handleBatchShip}
                  disabled={!selectedBox || !weight || parseFloat(weight) <= 0 || batchProcessing}
                >
                  {batchProcessing
                    ? `Processing ${batchProgress.processed}/${batchProgress.total}...`
                    : `Batch Ship ${batchCount} Order${batchCount !== 1 ? 's' : ''}`
                  }
                </button>
              ) : (
                <button
                  className="pack-ship-btn"
                  onClick={handleShipIt}
                  disabled={!allPackagesReady || shipping}
                  style={isLocalOrPickup ? { background: '#2e7d32', borderColor: '#1b5e20' } : undefined}
                >
                  {shipping
                    ? shippingStep || 'Processing...'
                    : isLocalOrPickup
                      ? `Complete ${localOrPickupLabel} (no UPS)`
                      : packages.length > 1
                      ? `Ship All (${packages.length} boxes)`
                      : 'Ship It'
                  }
                </button>
              )}
            </div>
          )}
        </div>

        {/* Insufficient inventory modal — NetSuite blocked the fulfillment because a
            packed SKU is short on-hand. Let the operator correct it right here and
            retry without buying a second UPS label. */}
        {insufficientInventory && (
          <div className="pack-confirm-overlay" onClick={() => setInsufficientInventory(null)}>
            <div className="pack-confirm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
              <div className="pack-confirm-header">Can't Ship — Inventory Short</div>
              {insufficientInventory.items.length === 0 ? (
                <div className="pack-confirm-desc">
                  NetSuite reported insufficient inventory but didn't say which item — check the order in NetSuite.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, margin: '12px 0' }}>
                  {insufficientInventory.items.map(item => (
                    <div key={item.sku} style={{ border: '1px solid #eee', borderRadius: 8, padding: 12 }}>
                      <div style={{ fontWeight: 600 }}>{item.sku}</div>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: 8 }}>
                        Need {item.needed}, {item.available} available
                        {item.adjusted ? ' — on-hand set' : ''}
                        {item.adjusted && item.fallbackBinCode ? ` (no preferred bin — posted to ${item.fallbackBinCode})` : ''}
                        {item.adjusted && item.unbinned ? ' (no bin on record — posted unbinned)' : ''}
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontSize: '0.85rem', color: '#666' }}>Set on-hand to:</span>
                        <input
                          type="number"
                          min="0"
                          value={adjustInputs[item.sku] ?? ''}
                          onChange={e => setAdjustInputs(prev => ({ ...prev, [item.sku]: e.target.value }))}
                          style={{ width: 80, padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc' }}
                        />
                        <button
                          className="pack-confirm-ok"
                          style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                          disabled={adjustingSku === item.sku}
                          onClick={() => handleQuickAdjust(item.sku)}
                        >
                          {adjustingSku === item.sku ? 'Posting...' : item.adjusted ? 'Update' : 'Post Adjustment'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {adjustError && (
                <div style={{ color: '#c62828', background: '#ffebee', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: '0.85rem' }}>
                  {adjustError}
                </div>
              )}
              <div className="pack-confirm-actions" style={{ flexWrap: 'wrap', gap: 8 }}>
                <button className="pack-confirm-cancel" onClick={() => setInsufficientInventory(null)}>
                  Cancel
                </button>
                <button
                  className="pack-confirm-cancel"
                  style={{ color: '#c62828', borderColor: '#c62828' }}
                  title="We really don't have this stock — ship without adjusting inventory. NetSuite gets caught up later."
                  onClick={handleShipAnyway}
                >
                  Ship Anyway (no stock)
                </button>
                <button className="pack-confirm-ok" onClick={handleRetryAfterAdjust}>
                  Retry Shipment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {shipping && (
          <div className="pack-loading-overlay">
            <div className="pack-loader">{shippingStep || 'Processing...'}</div>
          </div>
        )}
      </div>
    );
  }

  if (mode === 'storeShip' && storeShipment) {
    return (
      <StoreShipmentPack
        key={storeShipment.id}
        shipment={storeShipment}
        printerIp={printerIp}
        qzConnected={qzConnected}
        qzSelectedPrinter={qzSelectedPrinter}
        sounds={{ error: playError, complete: playComplete }}
        onDone={() => { setStoreShipment(null); setMode('queue'); }}
      />
    );
  }

  // Queue mode
  if (mode === 'queue') {
    return (
      <div className="pack-station">
        {/* Sticky header bar */}
        <div className="pack-topbar">
          <span className="pack-topbar-title">Pack Station</span>
          <div className="pack-topbar-scan">
            <ScanInput
              ref={scanInputRef}
              onScan={handleTicketScan}
              placeholder="Scan SO# or store box QR..."
              disabled={loading}
            />
          </div>
          <div className="pack-topbar-user">
            <button
              className={`pack-printer-btn ${printerIp || laserPrinter ? 'configured' : ''}`}
              onClick={() => setShowPrinterConfig(!showPrinterConfig)}
              title={printerIp ? `Zebra: ${printerIp}` : 'Configure printers'}
            >
              {printerIp || laserPrinter ? 'Printers' : 'No Printer'}
            </button>
            <span className="pack-topbar-name">{currentOperator.first_name}</span>
            <button className="pack-topbar-logout" onClick={handleLogout} title="Sign Out">
              <LogOut size={16} />
            </button>
          </div>
        </div>

        {showPrinterConfig && (
          <div className="pack-printer-config">
            <div className="pack-printer-config-row">
              <label>Zebra IP:</label>
              <input
                type="text"
                value={printerIp}
                onChange={(e) => setPrinterIp(e.target.value)}
                placeholder="e.g. 192.168.1.100"
                className="pack-printer-config-input"
              />
            </div>
            <div className="pack-printer-config-row">
              <label>Laser (Slips):</label>
              <select
                value={laserPrinter}
                onChange={(e) => setLaserPrinter(e.target.value)}
                className="pack-printer-config-select"
              >
                <option value="">-- None --</option>
                {availablePrinters.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <button
              className="pack-printer-save-btn"
              onClick={() => {
                localStorage.setItem('zebraPrinterIp', printerIp);
                localStorage.setItem('laserPrinter', laserPrinter);
                setShowPrinterConfig(false);
                setMessage(laserPrinter ? `Laser: ${laserPrinter}` : 'Printers saved');
              }}
            >
              Save
            </button>
          </div>
        )}

        <div className="pack-dashboard" onClick={resetActivity}>
          {/* Stats */}
          <div className="pack-stats-bar">
            <div className="pack-stat-card">
              <span className="pack-stat-value">{stats.packed_today || 0}</span>
              <span className="pack-stat-label">Packed Today</span>
            </div>
            <div className="pack-stat-card">
              <span className="pack-stat-value">{stats.in_progress || 0}</span>
              <span className="pack-stat-label">In Progress</span>
            </div>
            <div className="pack-stat-card">
              <span className="pack-stat-value">{stats.queue_count || 0}</span>
              <span className="pack-stat-label">In Queue</span>
            </div>
          </div>

          {(error || message) && <div className={`pack-message ${error ? 'error' : 'success'}`}>{error || message}</div>}

          {/* View toggle */}
          <div className="pack-view-toggle">
            <button
              className={`pack-toggle-btn ${!showRecentShipments ? 'active' : ''}`}
              onClick={() => setShowRecentShipments(false)}
            >
              Queue ({stats.queue_count || 0})
            </button>
            <button
              className={`pack-toggle-btn ${showRecentShipments ? 'active' : ''}`}
              onClick={handleToggleRecent}
            >
              Recent Shipments
            </button>
          </div>

          {/* Recent Shipments Table */}
          {showRecentShipments ? (
            <div className="pack-table-container">
              {/* Stuck tickets banner — surfaces orphaned packing sessions (e.g. ECONNRESET silent failures) */}
              {stuckTickets.length > 0 && (
                <div style={{ background: '#fff4e5', border: '1px solid #ed7d31', borderRadius: '6px', padding: '12px 16px', marginBottom: '12px' }}>
                  <div style={{ fontWeight: 600, color: '#8a4a00', marginBottom: '6px' }}>
                    {stuckTickets.length} stuck packing session{stuckTickets.length === 1 ? '' : 's'}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#8a4a00', marginBottom: '8px' }}>
                    Started but never completed (likely silent NetSuite/UPS failure). Reset to re-pack, or check NetSuite for an orphan IF.
                  </div>
                  <table style={{ width: '100%', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ textAlign: 'left' }}>
                        <th>Order</th><th>Customer</th><th>Packer</th><th>Stuck for</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stuckTickets.map(t => (
                        <tr key={t.id}>
                          <td style={{ fontWeight: 600 }}>{t.tran_id}</td>
                          <td>{t.customer_name}</td>
                          <td>{t.packer_name || '-'}</td>
                          <td>{t.minutes_stuck > 60 ? `${Math.round(t.minutes_stuck / 60)}h` : `${t.minutes_stuck}m`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Search bar */}
              <form onSubmit={handleRecentSearch} className="pack-recent-search">
                <input
                  type="text"
                  value={recentSearch}
                  onChange={(e) => setRecentSearch(e.target.value)}
                  placeholder="Search by order #, customer, or tracking..."
                  className="pack-recent-search-input"
                />
                <button type="submit" className="pack-recent-search-btn">Search</button>
                {recentSearch && (
                  <button type="button" className="pack-recent-search-btn pack-recent-clear-btn" onClick={() => { setRecentSearch(''); fetchRecentShipments(1, ''); }}>Clear</button>
                )}
              </form>

              {loadingRecent ? (
                <div className="pack-loading">Loading recent shipments...</div>
              ) : recentShipments.length === 0 ? (
                <div className="pack-empty">
                  <p className="pack-empty-message">{recentSearch ? 'No shipments match your search' : 'No recent shipments'}</p>
                </div>
              ) : (
                <>
                <table className="pack-table pack-recent-table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Customer</th>
                      <th>Tracking</th>
                      <th>Shipped</th>
                      <th>Cost</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentShipments.map(s => (
                      <tr key={s.id} className={s.status === 'voided' ? 'pack-row-voided' : ''}>
                        <td className="pack-order-number">{s.tran_id}</td>
                        <td className="pack-customer-name">{s.customer_name}</td>
                        <td className="pack-tracking-cell">
                          {(s.all_tracking_numbers || []).length > 0 ? (
                            <div className="pack-tracking-list">
                              {s.all_tracking_numbers.map((tn, i) => (
                                <span key={i} className="pack-tracking-wrap">
                                  <span className="pack-tracking-mono">{tn}</span>
                                  <button
                                    className="pack-copy-btn"
                                    onClick={() => handleCopyTracking(`${s.id}-${i}`, tn)}
                                    title="Copy tracking number"
                                  >
                                    {copiedTrackingId === `${s.id}-${i}` ? <Check size={13} /> : <Copy size={13} />}
                                  </button>
                                </span>
                              ))}
                            </div>
                          ) : '-'}
                        </td>
                        <td>{getTimeSince(s.fulfilled_at)} ago</td>
                        <td>
                          {s.shipping_cost ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontWeight: 600 }}>${parseFloat(s.shipping_cost).toFixed(2)}</span>
                              {s.retail_shipping_cost && parseFloat(s.retail_shipping_cost) > parseFloat(s.shipping_cost) && (
                                <>
                                  <span style={{ fontSize: '0.75rem', color: '#aaa', textDecoration: 'line-through' }}>${parseFloat(s.retail_shipping_cost).toFixed(2)}</span>
                                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#2e7d32' }}>
                                    -{Math.round((1 - parseFloat(s.shipping_cost) / parseFloat(s.retail_shipping_cost)) * 100)}%
                                  </span>
                                </>
                              )}
                            </span>
                          ) : '-'}
                        </td>
                        <td>
                          <span className={`pack-status-badge ${s.status}`}>
                            {s.status}
                          </span>
                        </td>
                        <td className="pack-recent-actions">
                          {s.status === 'fulfilled' && (
                            <>
                              {s.has_label ? (
                                <button
                                  className="pack-recent-action-btn"
                                  onClick={() => handleReprintLabel(s)}
                                  title="Reprint shipping label"
                                >
                                  Label
                                </button>
                              ) : (
                                <button
                                  className="pack-recent-action-btn"
                                  onClick={() => handleRegenerateLabel(s)}
                                  disabled={regeneratingId === s.id}
                                  title="No saved label — void any old labels and generate a fresh one"
                                  style={{ background: '#ed7d31', color: 'white' }}
                                >
                                  {regeneratingId === s.id ? '...' : 'Regenerate'}
                                </button>
                              )}
                              <button
                                className="pack-recent-action-btn"
                                onClick={() => handleReprintSlip(s)}
                                title="Reprint packing slip"
                              >
                                Slip
                              </button>
                              <button
                                className="pack-recent-action-btn pack-void-btn"
                                onClick={() => handleVoidShipment(s)}
                                disabled={voidingId === s.id || reshippingId === s.id}
                                title="Void this shipment"
                              >
                                {voidingId === s.id ? '...' : 'Void'}
                              </button>
                              <button
                                className="pack-recent-action-btn pack-void-btn"
                                onClick={() => handleVoidAndReship(s)}
                                disabled={voidingId === s.id || reshippingId === s.id}
                                title="Void this shipment and put it back in the pick queue for an immediate reship — only before carrier pickup"
                                style={{ background: '#c0392b', color: 'white' }}
                              >
                                {reshippingId === s.id ? '...' : 'Void & Reship'}
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {recentTotal > recentLimit && (
                  <div className="pack-recent-pagination">
                    <button
                      className="pack-recent-page-btn"
                      disabled={recentPage <= 1}
                      onClick={() => handleRecentPageChange(recentPage - 1)}
                    >
                      Prev
                    </button>
                    <span className="pack-recent-page-info">
                      Page {recentPage} of {Math.ceil(recentTotal / recentLimit)} ({recentTotal} shipments)
                    </span>
                    <button
                      className="pack-recent-page-btn"
                      disabled={recentPage >= Math.ceil(recentTotal / recentLimit)}
                      onClick={() => handleRecentPageChange(recentPage + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}
                </>
              )}
            </div>
          ) : (
            /* Queue Table */
            <div className="pack-table-container">
              {loading ? (
                <div className="pack-loading">Loading queue...</div>
              ) : queue.length === 0 ? (
                <div className="pack-empty">
                  <p className="pack-empty-message">No tickets ready to pack</p>
                  <p className="pack-empty-hint">Dispatched pick tickets will appear here</p>
                </div>
              ) : (
                <table className="pack-table">
                  <thead>
                    <tr>
                      <th>Ticket</th>
                      <th>Order</th>
                      <th>Customer</th>
                      <th>Type</th>
                      <th>Items</th>
                      <th>Age</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queue.map(ticket => (
                      <tr key={ticket.id}>
                        <td className="pack-order-number">{ticket.ticket_number}</td>
                        <td>{ticket.tran_id}</td>
                        <td className="pack-customer-name">{ticket.customer_name}</td>
                        <td>
                          {ticket.customer_category ? (
                            <span className={`pack-category-badge ${getCategoryClass(ticket.customer_category)}`}>
                              {getCategoryLabel(ticket.customer_category)}
                            </span>
                          ) : '-'}
                        </td>
                        <td>{ticket.total_line_items || 0}</td>
                        <td>
                          <span className="pack-age">{getTimeSince(ticket.dispatched_at)}</span>
                        </td>
                        <td>
                          <button
                            className="pack-action-btn"
                            onClick={() => startPacking(ticket.ticket_number)}
                          >
                            Pack
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Item scanning mode
  if (mode === 'items') {
    const totalRequired = salesOrder ? salesOrder.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0) : 0;
    const allScanned = scannedItems.length === totalRequired && totalRequired > 0;

    return (
      <div className="pack-station">
        {/* Sticky header bar */}
        <div className="pack-topbar">
          <span className="pack-topbar-title">Packing: {currentTicket?.tran_id}</span>
          <div className="pack-topbar-scan">
            <ScanInput
              ref={scanInputRef}
              onScan={handleItemScan}
              placeholder="Scan item SKU..."
              disabled={false}
            />
          </div>
          <div className="pack-topbar-user">
            {scannedItems.length > 0 && (
              <button className="pack-undo-btn" onClick={handleUndo}>Undo</button>
            )}
            <button className="pack-cancel-btn" onClick={handleCancelPacking}>Cancel</button>
          </div>
        </div>

        <div className="pack-dashboard" onClick={resetActivity}>
          {/* Customer info */}
          <div className="pack-context-bar">
            <span className="pack-context-customer">{salesOrder?.customerName || currentTicket?.customer_name}</span>
            <span className="pack-context-ticket">{currentTicket?.ticket_number}</span>
          </div>

          {/* Batch detection banner */}
          {batchOrders.length > 0 && !batchMode && (
            <div className="pack-batch-banner" onClick={handleEnterBatchMode}>
              <Layers size={20} />
              <div className="pack-batch-banner-text">
                <strong>{batchOrders.length} identical order{batchOrders.length !== 1 ? 's' : ''} pending</strong>
                <span>Tap to batch fulfill with same box and weight</span>
              </div>
              <button className="pack-batch-banner-btn">Batch Fulfill</button>
            </div>
          )}

          {/* Message */}
          {(error || message) && (
            <div className={`pack-message ${error ? 'error' : 'success'}`}>
              {error || message}
            </div>
          )}

          {/* Order Display - tap items to manually fulfill */}
          {salesOrder && (
            <OrderDisplay
              order={salesOrder}
              scannedItems={scannedItems}
              mode="items"
              onItemTap={handleItemTap}
            />
          )}

          {/* Action Buttons */}
          {salesOrder && (
            <div className="pack-scanning-actions">
              <div className="pack-progress-info">
                <span className="pack-scanned-count">
                  {scannedItems.length} / {totalRequired} items
                </span>
              </div>
              <div className="pack-action-buttons">
                <button
                  className="pack-incomplete-button"
                  onClick={handleProceedToBoxing}
                  disabled={scannedItems.length === 0}
                >
                  Fulfill Incomplete & Box
                </button>
                {allScanned && (
                  <button
                    className="pack-proceed-button"
                    onClick={handleProceedToBoxing}
                  >
                    All Items - Proceed to Boxing
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Confirm fulfill modal */}
        {confirmItem && (
          <div className="pack-confirm-overlay" onClick={() => setConfirmItem(null)}>
            <div className="pack-confirm-modal" onClick={e => e.stopPropagation()}>
              <div className="pack-confirm-header">Confirm Item</div>
              <div className="pack-confirm-sku">{confirmItem.sku}</div>
              <div className="pack-confirm-desc">{confirmItem.description}</div>
              <div className="pack-confirm-qty-row">
                <button
                  className="pack-confirm-qty-btn"
                  onClick={() => setConfirmQty(Math.max(1, confirmQty - 1))}
                >
                  -
                </button>
                <span className="pack-confirm-qty-value">{confirmQty}</span>
                <button
                  className="pack-confirm-qty-btn"
                  onClick={() => {
                    const remaining = Number(confirmItem.quantity) - scannedItems.filter(s => s.sku === confirmItem.sku).length;
                    setConfirmQty(Math.min(remaining, confirmQty + 1));
                  }}
                >
                  +
                </button>
              </div>
              <div className="pack-confirm-actions">
                <button className="pack-confirm-cancel" onClick={() => setConfirmItem(null)}>
                  Cancel
                </button>
                <button className="pack-confirm-ok" onClick={handleConfirmFulfill}>
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {loading && (
          <div className="pack-loading-overlay">
            <div className="pack-loader">Processing...</div>
          </div>
        )}
      </div>
    );
  }

  // Complete mode - success screen (multi-box aware)
  // FBA Complete Mode
  if (mode === 'complete' && isFbaOrder && fulfillmentResult?.type === 'fba') {
    return (
      <div className="pack-station">
        <div className="pack-dashboard">
          <div className="pack-complete-section">
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>
                <Check size={64} color="#283593" />
              </div>
              <h2 style={{ color: '#283593', margin: '0 0 8px' }}>FBA Packing Complete</h2>
              <p style={{ color: '#666', margin: '0 0 24px' }}>
                {fulfillmentResult.cartonCount} cartons, {fulfillmentResult.totalUnits} units
              </p>

              <div style={{ background: '#f5f5f5', borderRadius: 8, padding: 16, marginBottom: 24, fontSize: 14 }}>
                <div><strong>Plan ID:</strong> {fulfillmentResult.planId}</div>
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 24 }}>
                <button
                  style={{ padding: '10px 20px', background: '#e8eaf6', color: '#283593', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                  onClick={async () => {
                    try {
                      const res = await axios.get(`/api/packing/fba-labels/${currentTicket.id}`);
                      if (res.data.fnskuPdf) {
                        const blob = new Blob([Uint8Array.from(atob(res.data.fnskuPdf), c => c.charCodeAt(0))], { type: 'application/pdf' });
                        window.open(URL.createObjectURL(blob), '_blank');
                      }
                    } catch (err) { console.error(err); }
                  }}
                >
                  Reprint FNSKU Labels
                </button>
                <button
                  style={{ padding: '10px 20px', background: '#fff8e1', color: '#f57f17', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                  onClick={async () => {
                    try {
                      const res = await axios.get(`/api/packing/fba-labels/${currentTicket.id}`);
                      if (res.data.cartonLabels?.[0]?.downloadUrl) {
                        window.open(res.data.cartonLabels[0].downloadUrl, '_blank');
                      }
                    } catch (err) { console.error(err); }
                  }}
                >
                  Reprint Carton Labels
                </button>
              </div>

              <button className="pack-next-btn" onClick={resetPackFlow}>
                Pack Next Order
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'complete') {
    const aggregatedItems = getAggregatedItems();
    const allLabelPackages = labelData?.packages || [labelData];
    const boxCount = allLabelPackages.length;
    const totalCost = labelData?.totalCost || (labelData?.cost ? parseFloat(labelData.cost) : 0);

    return (
      <div className="pack-station">
        <div className="pack-dashboard">
          <div className="pack-complete-section">
            <h2>Order Shipped{boxCount > 1 ? ` (${boxCount} Boxes)` : ''}</h2>
            <p className="pack-complete-order">{currentTicket?.tran_id}</p>

            {/* Tracking numbers - show all for multi-box */}
            {allLabelPackages.map((lp, i) => lp?.trackingNumber && (
              <div key={i} className="pack-complete-tracking">
                <span className="pack-tracking-label">{boxCount > 1 ? `Box ${i + 1}:` : 'Tracking:'}</span>
                <span className="pack-tracking-number">{lp.trackingNumber}</span>
              </div>
            ))}

            {totalCost > 0 && (
              <p className="pack-complete-cost">
                Total Label Cost: ${totalCost.toFixed(2)}
                {boxCount > 1 && ` (${boxCount} labels)`}
              </p>
            )}

            <div className="pack-success-actions">
              {allLabelPackages.some(p => p?.labelData) && (
                <button className="pack-print-label-btn" onClick={printLabel}>
                  {boxCount > 1 ? `Print All Labels (${boxCount})` : 'Print Label'}
                </button>
              )}
              {isLocalOrPickup && (
                <button
                  className="pack-print-label-btn"
                  onClick={async () => {
                    const ok = await printDeliveryTag();
                    setMessage(ok ? 'Delivery tag sent to printer' : 'Could not print delivery tag — check Zebra IP');
                  }}
                >
                  {packages.length > 1 ? `Print Tags (${packages.length})` : 'Print Tag'}
                </button>
              )}
              <button className="pack-print-slip-btn" onClick={printPackingSlip}>
                Print Packing Slip
              </button>
              <button className="pack-next-btn" onClick={resetPackFlow}>
                Pack Next
              </button>
            </div>

            <div className="pack-success-actions pack-success-actions-danger">
              <button
                className="pack-edit-order-btn"
                onClick={handleEditJustShippedOrder}
                disabled={voidingId === currentTicket?.id || reshippingId === currentTicket?.id}
                title="Void the label(s), reverse the NetSuite fulfillment, and put this order back in the pick queue to repack — e.g. a forgotten box"
              >
                {reshippingId === currentTicket?.id ? 'Reopening...' : 'Edit Order'}
              </button>
              <button
                className="pack-void-order-btn"
                onClick={handleVoidJustShippedOrder}
                disabled={voidingId === currentTicket?.id || reshippingId === currentTicket?.id}
                title="Void the label(s) — use this if the order wasn't supposed to ship"
              >
                {voidingId === currentTicket?.id ? 'Voiding...' : 'Void Order'}
              </button>
            </div>
          </div>
        </div>

        {/* Packing Slip - Print Only */}
        <div className="pack-print-only">
          <div className="pack-slip">
            <div className="pack-slip-header">
              <h1>PACKING LIST</h1>
              <div className="pack-slip-company">Daydreamer Concepts</div>
            </div>

            <div className="pack-slip-info">
              <div className="pack-slip-section">
                <h4>Ship To:</h4>
                <div>{salesOrder?.shipTo?.name || salesOrder?.customerName}</div>
                {salesOrder?.shipTo?.addr1 && <div>{salesOrder.shipTo.addr1}</div>}
                {salesOrder?.shipTo?.addr2 && <div>{salesOrder.shipTo.addr2}</div>}
                {salesOrder?.shipTo?.city && (
                  <div>
                    {salesOrder.shipTo.city}, {salesOrder.shipTo.state} {salesOrder.shipTo.zip}
                  </div>
                )}
                {salesOrder?.shipTo?.country && salesOrder.shipTo.country !== 'US' && (
                  <div>{salesOrder.shipTo.country}</div>
                )}
              </div>

              <div className="pack-slip-section">
                <h4>Order Information:</h4>
                <div>Order #: {salesOrder?.orderNumber}</div>
                <div>Date: {new Date().toLocaleDateString()}</div>
                {fulfillmentResult?.fulfillmentId && (
                  <div>Fulfillment #: {fulfillmentResult.fulfillmentId}</div>
                )}
                {allLabelPackages.map((lp, i) => lp?.trackingNumber && (
                  <div key={i}>
                    {boxCount > 1 ? `Tracking ${i + 1}: ` : 'Tracking #: '}{lp.trackingNumber}
                  </div>
                ))}
              </div>
            </div>

            <table className="pack-slip-items">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Description</th>
                  <th>Qty</th>
                </tr>
              </thead>
              <tbody>
                {aggregatedItems.map((item, idx) => (
                  <tr key={idx}>
                    <td>{item.sku}</td>
                    <td>{item.description}</td>
                    <td>{item.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pack-slip-footer">
              <div>Total Items: {scannedItems.length}</div>
              <div>Boxes: {boxCount}</div>
              <div className="pack-slip-thanks">Thank you for your order!</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default PackStation;
